<template>
  <HeaderApp />

  <!-- in ltr change (md:mr-60 to md:ml-60)-->
  <div
    :class="
      'mx-2 mt-14 mb-5 h-full bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 ' +
      ($store.getters['isRtl']
        ? $store.state.navbar.isSidebarCollapsed
          ? ' md:mr-20 '
          : ' md:mr-60 '
        : $store.state.navbar.isSidebarCollapsed
        ? ' md:ml-20  '
        : ' md:ml-60  ')
    "
    v-if="!isLoadingData"
  >
    <!-- Header Section with Tabs -->
    <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
            {{ $t("dashboard") }}
          </h1>
        </div>
        <div class="flex items-center space-x-2">
          <button
            @click="toggleNumbersVisibility"
            :class="[
              'p-2 rounded-lg transition-all duration-200 hover:bg-gray-100 dark:hover:bg-gray-700',
              showNumbers 
                ? 'text-gray-600 dark:text-gray-400' 
                : 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
            ]"
            :title="showNumbers ? $t('hide_numbers') : $t('show_numbers')"
          >
            <i :class="showNumbers ? 'fa-solid fa-eye' : 'fa-solid fa-eye-slash'"></i>
          </button>
        </div>
      </div>

      <!-- Tabs -->
      <div class="mt-4">
        <div class="flex space-x-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
          <button
            @click="activeTab = 'dashboard'"
            :class="[
              'px-4 py-2 text-sm font-medium rounded-md transition-all duration-200',
              activeTab === 'dashboard'
                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200',
            ]"
          >
            <i class="fa-solid fa-chart-line mr-2"></i>
            {{ $t("dashboard") }}
          </button>
          <button
            @click="activeTab = 'pages'"
            :class="[
              'px-4 py-2 text-sm font-medium rounded-md transition-all duration-200',
              activeTab === 'pages'
                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200',
            ]"
          >
            <i class="fa-solid fa-th-large mr-2"></i>
            {{ $t("pages") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Period Filter Section (Dashboard Tab Only) -->
    <div
      v-if="activeTab === 'dashboard'"
      class="px-6 py-4 border-b border-gray-200 dark:border-gray-700"
    >
      <div class="flex flex-wrap items-center gap-2">
        <button
          class="px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200"
          :class="{
            'bg-blue-600 text-white shadow-md hover:bg-blue-700':
              period === 'today',
            'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600':
              period !== 'today',
          }"
          @click="periodChanged('today')"
        >
          {{ $t("today") }}
        </button>
        <button
          class="px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200"
          :class="{
            'bg-blue-600 text-white shadow-md hover:bg-blue-700':
              period === 'yesterday',
            'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600':
              period !== 'yesterday',
          }"
          @click="periodChanged('yesterday')"
        >
          {{ $t("yesterday") }}
        </button>
        <button
          class="px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200"
          :class="{
            'bg-blue-600 text-white shadow-md hover:bg-blue-700':
              period === 'last7days',
            'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600':
              period !== 'last7days',
          }"
          @click="periodChanged('last7days')"
        >
          {{ $t("last7days") }}
        </button>
        <button
          class="px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200"
          :class="{
            'bg-blue-600 text-white shadow-md hover:bg-blue-700':
              period === 'last30days',
            'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600':
              period !== 'last30days',
          }"
          @click="periodChanged('last30days')"
        >
          {{ $t("last30days") }}
        </button>
        <button
          class="px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200"
          :class="{
            'bg-blue-600 text-white shadow-md hover:bg-blue-700':
              period === 'thismonth',
            'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600':
              period !== 'thismonth',
          }"
          @click="periodChanged('thismonth')"
        >
          {{ $t("thismonth") }}
        </button>
        <button
          class="px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200"
          :class="{
            'bg-blue-600 text-white shadow-md hover:bg-blue-700':
              period === 'lastmonth',
            'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600':
              period !== 'lastmonth',
          }"
          @click="periodChanged('lastmonth')"
        >
          {{ $t("lastmonth") }}
        </button>
        <button
          class="px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200"
          :class="{
            'bg-blue-600 text-white shadow-md hover:bg-blue-700':
              period === 'lastyear',
            'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600':
              period !== 'lastyear',
          }"
          @click="periodChanged('lastyear')"
        >
          {{ $t("lastyear") }}
        </button>
        <button
          class="px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200"
          :class="{
            'bg-blue-600 text-white shadow-md hover:bg-blue-700':
              period === 'thisyear',
            'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600':
              period !== 'thisyear',
          }"
          @click="periodChanged('thisyear')"
        >
          {{ $t("thisyear") }}
        </button>
        <button
          class="px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200"
          :class="{
            'bg-blue-600 text-white shadow-md hover:bg-blue-700':
              period === 'all',
            'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600':
              period !== 'all',
          }"
          @click="periodChanged('all')"
        >
          {{ $t("all_time") }}
        </button>
        
        <!-- Custom Date Range Button -->
        <button
          class="px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200"
          :class="{
            'bg-blue-600 text-white shadow-md hover:bg-blue-700':
              period === 'custom',
            'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600':
              period !== 'custom',
          }"
          @click="showDateRangeDialog = true"
        >
          <i class="fa-solid fa-calendar-days mr-2"></i>
          {{ $t("custom_date_range") }}
        </button>
      </div>
    </div>

    <!-- Dashboard Cards Section -->
    <div v-if="activeTab === 'dashboard'" class="px-6 py-6">
      <div
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6"
      >
        <!-- Total Expense Card -->
        <ReportHomeCard
          :title="$t('total_expense')"
          :dollarValue="formatNumber(items.expense.total_amount_dollar)"
          :dinarValue="formatNumber(items.expense.total_amount_dinar)"
          :showNumbers="showNumbers"
          colorName="red"
          iconClass="fa-solid fa-sack-dollar"
          classColor="from-red-50 to-red-100"
        />

        <!-- Total Income Card -->
        <ReportHomeCard
          :title="$t('total_income')"
          :dollarValue="formatNumber(items.income.total_amount_dollar)"
          :dinarValue="formatNumber(items.income.total_amount_dinar)"
          :showNumbers="showNumbers"
          colorName="purple"
          iconClass="fa-solid fa-hand-holding-dollar"
          classColor="from-purple-50 to-purple-100"
        />

        <!-- Total Purchase Card -->
        <ReportHomeCard
          :title="$t('total_purchase')"
          :dollarValue="
            formatNumber(items.purchase.total_purchase_amount_dollar)
          "
          :dinarValue="formatNumber(items.purchase.total_purchase_amount_dinar)"
          :showNumbers="showNumbers"
          colorName="yellow"
          iconClass="fa-solid fa-shopping-cart"
          classColor="from-yellow-50 to-yellow-100"
        />

        <!-- Total Sale Card -->
        <ReportHomeCard
          :title="$t('total_sale')"
          :dollarValue="
            formatNumber(items.saleAndProfit.total_sale_price_dollar)
          "
          :dinarValue="formatNumber(items.saleAndProfit.total_sale_price_dinar)"
          :showNumbers="showNumbers"
          colorName="purple"
          iconClass="fa-solid fa-coins"
          classColor="from-purple-50 to-purple-100"
        />

        <!-- Total Sale Profit Card -->
        <ReportHomeCard
          :title="$t('total_sale_profit')"
          :dollarValue="formatNumber(items.saleAndProfit.total_profit_dollar)"
          :dinarValue="formatNumber(items.saleAndProfit.total_profit_dinar)"
          :showNumbers="showNumbers"
          colorName="green"
          iconClass="fa-solid fa-circle-dollar-to-slot"
          classColor="from-green-50 to-green-100"
        />

        <!-- Total Profit in this Period Card -->
        <ReportHomeCard
          :title="$t('total_profit_in_this_period')"
          :dollarValue="formatNumber(items.total_profit.total_profit_dollar)"
          :dinarValue="formatNumber(items.total_profit.total_profit_dinar)"
          :showNumbers="showNumbers"
          colorName="green"
          iconClass="fa-solid fa-circle-dollar-to-slot"
          classColor="from-green-50 to-green-100"
        />

        <!-- Total Returned Invoice Card -->
        <ReportHomeCard
          :title="$t('total_returned_invoice')"
          :dollarValue="
            formatNumber(items.returned_invoice.total_returned_amount_dollar)
          "
          :dinarValue="
            formatNumber(items.returned_invoice.total_returned_amount_dinar)
          "
          :showNumbers="showNumbers"
          colorName="red"
          iconClass="fa-solid fa-hand-holding-dollar"
          classColor="from-red-50 to-red-100"
        />

        <!-- Total Waste Cost Card -->
        <ReportHomeCard
          :title="$t('total_waste_cost')"
          :dollarValue="formatNumber(items.waste_cost.total_waste_cost_dollar)"
          :dinarValue="formatNumber(items.waste_cost.total_waste_cost_dinar)"
          :showNumbers="showNumbers"
          colorName="red"
          iconClass="fa-solid fa-chain-broken"
          classColor="from-red-50 to-red-100"
        />

        <!-- Total Debts I Owe People Card -->
        <ReportHomeCard
          :title="$t('total_debts_i_owe_people')"
          :dollarValue="formatNumber(iOweUsd)"
          :dinarValue="formatNumber(iOweIqd)"
          :showNumbers="showNumbers"
          colorName="indigo"
          iconClass="fa-solid fa-hand-holding-dollar"
          classColor="from-indigo-50 to-indigo-100"
        />

        <!-- Total Debts They Owe Me Card -->
        <ReportHomeCard
          :title="$t('total_debts_they_owe_me')"
          :dollarValue="formatNumber(theyOweUsd)"
          :dinarValue="formatNumber(theyOweIqd)"
          :showNumbers="showNumbers"
          colorName="red"
          iconClass="fa-solid fa-circle-dollar-to-slot"
          classColor="from-red-50 to-red-100"
        />
      </div>

      <!-- Sales Trend Chart
      <div class="mt-8">
        <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              {{ $t("sales_trend") }}
            </h3>
            <div class="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
              <i class="fa-solid fa-chart-line"></i>
              <span>{{ getDateRangeText() }}</span>
            </div>
          </div>
          
          <div v-if="Object.keys(salesTrend).length > 0" class="chart-container">
            <apex-chart
              type="line"
              height="380"
              :options="salesTrendChartOptions"
              :series="salesTrendData"
            ></apex-chart>
          </div>
          
          <div v-else-if="!isLoadingData" class="py-8 text-center">
            <i class="fa-solid fa-chart-line text-4xl text-gray-300 dark:text-gray-600 mb-4"></i>
            <p class="text-gray-500 dark:text-gray-400">
              {{ $t("no_sales_data") }}
            </p>
          </div>
          
          <div v-else class="py-8 text-center">
            <img
              src="../../assets/loading.webp"
              style="width: 70px; height: 70px"
              class="mx-auto"
            />
          </div>
        </div>
      </div> -->
    </div>

    <!-- Charts Section -->
    <div v-if="activeTab === 'dashboard'" class="px-6 py-6">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Sale Types Chart -->
        <div
          class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {{ $t("sale_types") }}
          </h3>
          <div class="chart-container items-center justify-center">
            <div class="chart-title">{{ $t("sale_types") }}</div>
            <center>
              <div class="">
                <apex-chart
                  width="300"
                  type="donut"
                  :options="chartOptions"
                  :series="series"
                  v-if="series.length > 0"
                ></apex-chart>
                <div v-if="series.length == 0 && !isLoadingData" class="py-8">
                  <p class="text-gray-500 dark:text-gray-400">
                    {{ $t("no_data_found") }}
                  </p>
                </div>
                <div v-if="series.length == 0 && isLoadingData" class="py-8">
                  <img
                    src="../../assets/loading.webp"
                    style="width: 70px; height: 70px"
                    class="mx-auto"
                  />
                </div>
              </div>
            </center>
            <div class="mt-6">
              <EasyDataTable
                :headers="headersSaleType"
                :items="items.sale_type"
                :body-text-direction="
                  $store.getters['isRtl'] ? 'right' : 'left'
                "
                alternating
                :header-props="{
                  'sort-icon': 'fa-solid fa-arrow-up-wide-short',
                }"
                :loading="isLoadingData"
                :class="getTableClass()"
                :table-class-name="getTableClassName()"
                :key="`table-${isDark ? 'dark' : 'light'}`"
                :hide-footer="true"
              >
                <template #default="{ column, sortDirection }">
                  <span
                    :class="`flex items-center font-semibold text-gray-700 dark:text-gray-300 ${
                      $store.getters['isRtl'] ? 'justify-end' : 'justify-start'
                    }`"
                  >
                    <span
                      :class="`custom-sort-icon ${
                        $store.getters['isRtl'] ? 'ml-2' : 'mr-2'
                      }`"
                      v-if="column.sortable"
                    >
                      <i
                        v-if="sortDirection === 'asc'"
                        class="fa-solid fa-arrow-up text-blue-500"
                      ></i>
                      <i
                        v-else-if="sortDirection === 'desc'"
                        class="fa-solid fa-arrow-down text-blue-500"
                      ></i>
                      <i
                        v-else
                        class="fa-solid fa-arrows-up-down text-gray-400"
                      ></i>
                    </span>
                    {{ column.label }}
                  </span>
                </template>

                <template #loading>
                  <img
                    src="../../assets/loading.webp"
                    style="width: 70px; height: 70px"
                  />
                </template>
                <template #item-item_quantity_sold="item">
                  <span>
                    {{ formatNumber(item.item_quantity_sold) }}
                    <span v-if="getItemUnitName(item)" class="text-gray-500 dark:text-gray-400 text-sm ml-1">
                      {{ getItemUnitName(item) }}
                    </span>
                  </span>
                </template>
                <template #item-total_sale_price_dinar="item">
                  <span>
                    {{ formatNumber(item.total_sale_price_dinar) }}
                  </span>
                </template>
                <template #item-total_profit_dinar="item">
                  <span>
                    {{ formatNumber(item.total_profit_dinar) }}
                  </span>
                </template>
                <template #item-total_discount_dinar="item">
                  <span>
                    {{ formatNumber(item.total_discount_dinar) }}
                  </span>
                </template>
                <template #item-total_sale_price_dollar="item">
                  <span>
                    {{ formatNumber(item.total_sale_price_dollar) }}
                  </span>
                </template>
                <template #item-total_profit_dollar="item">
                  <span>
                    {{ formatNumber(item.total_profit_dollar) }}
                  </span>
                </template>
                <template #item-total_discount_dollar="item">
                  <span>
                    {{ formatNumber(item.total_discount_dollar) }}
                  </span>
                </template>
                <template #item-total_cost_price_dinar="item">
                  <span>
                    {{ formatNumber(item.total_cost_price_dinar) }}
                  </span>
                </template>
                <template #item-total_cost_price_dollar="item">
                  <span>
                    {{ formatNumber(item.total_cost_price_dollar) }}
                  </span>
                </template>
                <template #empty-message>
                  <span class="text-textBlack1 dark:text-white">
                    {{ $t("no_data_found") }}
                  </span>
                </template>
                <template
                  #pagination="{ prevPage, nextPage, isFirstPage, isLastPage }"
                >
                  <button :disabled="isFirstPage" @click="prevPage">
                    <i
                      :class="
                        'fa-sharp fa-solid ' +
                        ($store.getters['isRtl']
                          ? ' fa-chevron-right'
                          : ' fa-chevron-left')
                      "
                    ></i>
                  </button>
                  <div class="w-5"></div>
                  <button :disabled="isLastPage" @click="nextPage">
                    <i
                      :class="
                        'fa-sharp fa-solid ' +
                        ($store.getters['isRtl']
                          ? ' fa-chevron-left'
                          : ' fa-chevron-right')
                      "
                    ></i>
                  </button>
                </template>
              </EasyDataTable>
            </div>
          </div>
        </div>

        <!-- Most Sold Items Chart -->
        <div
          class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {{ $t("most_sold_items") }}
          </h3>
          <div class="chart-container">
            <div class="chart-title">{{ $t("most_sold_items") }}</div>
            <center class="">
              <apex-chart
                width="300"
                type="donut"
                :options="chartOptionsItems"
                :series="seriesItems"
                v-if="seriesItems.length > 0"
              ></apex-chart>
              <div
                v-if="seriesItems.length == 0 && !isLoadingData"
                class="py-8"
              >
                <p class="text-gray-500 dark:text-gray-400">
                  {{ $t("no_data_found") }}
                </p>
              </div>
              <div v-if="seriesItems.length == 0 && isLoadingData" class="py-8">
                <img
                  src="../../assets/loading.webp"
                  style="width: 70px; height: 70px"
                  class="mx-auto"
                />
              </div>
            </center>
            <div class="mt-6">
              <EasyDataTable
                :headers="headersMostSoldItem"
                :items="items.item_sale"
                :body-text-direction="
                  $store.getters['isRtl'] ? 'right' : 'left'
                "
                alternating
                :header-props="{
                  'sort-icon': 'fa-solid fa-arrow-up-wide-short',
                }"
                :loading="isLoadingData"
                :class="getTableClass()"
                :table-class-name="getTableClassName()"
                :key="`table-${isDark ? 'dark' : 'light'}`"
                :hide-footer="true"
              >
                <template #default="{ column, sortDirection }">
                  <span
                    :class="`flex items-center font-semibold text-gray-700 dark:text-gray-300 ${
                      $store.getters['isRtl'] ? 'justify-end' : 'justify-start'
                    }`"
                  >
                    <span
                      :class="`custom-sort-icon ${
                        $store.getters['isRtl'] ? 'ml-2' : 'mr-2'
                      }`"
                      v-if="column.sortable"
                    >
                      <i
                        v-if="sortDirection === 'asc'"
                        class="fa-solid fa-arrow-up text-blue-500"
                      ></i>
                      <i
                        v-else-if="sortDirection === 'desc'"
                        class="fa-solid fa-arrow-down text-blue-500"
                      ></i>
                      <i
                        v-else
                        class="fa-solid fa-arrows-up-down text-gray-400"
                      ></i>
                    </span>
                    {{ column.label }}
                  </span>
                </template>

                <template #loading>
                  <img
                    src="../../assets/loading.webp"
                    style="width: 70px; height: 70px"
                  />
                </template>
                <template #item-item_quantity_sold="item">
                  <span>
                    {{ formatNumber(item.item_quantity_sold) }}
                    <span v-if="getItemUnitName(item)" class="text-gray-500 dark:text-gray-400 text-sm ml-1">
                      {{ getItemUnitName(item) }}
                    </span>
                  </span>
                </template>
                <template #item-total_sale_price_dinar="item">
                  <span>
                    {{ formatNumber(item.total_sale_price_dinar) }}
                  </span>
                </template>
                <template #item-total_profit_dinar="item">
                  <span>
                    {{ formatNumber(item.total_profit_dinar) }}
                  </span>
                </template>
                <template #item-total_discount_dinar="item">
                  <span>
                    {{ formatNumber(item.total_discount_dinar) }}
                  </span>
                </template>
                <template #item-total_sale_price_dollar="item">
                  <span>
                    {{ formatNumber(item.total_sale_price_dollar) }}
                  </span>
                </template>
                <template #item-total_profit_dollar="item">
                  <span>
                    {{ formatNumber(item.total_profit_dollar) }}
                  </span>
                </template>
                <template #item-total_discount_dollar="item">
                  <span>
                    {{ formatNumber(item.total_discount_dollar) }}
                  </span>
                </template>
                <template #item-total_cost_price_dinar="item">
                  <span>
                    {{ formatNumber(item.total_cost_price_dinar) }}
                  </span>
                </template>
                <template #item-total_cost_price_dollar="item">
                  <span>
                    {{ formatNumber(item.total_cost_price_dollar) }}
                  </span>
                </template>
                <template #empty-message>
                  <span class="text-textBlack1 dark:text-white">
                    {{ $t("no_data_found") }}
                  </span>
                </template>
                <template
                  #pagination="{ prevPage, nextPage, isFirstPage, isLastPage }"
                >
                  <button :disabled="isFirstPage" @click="prevPage">
                    <i
                      :class="
                        'fa-sharp fa-solid ' +
                        ($store.getters['isRtl']
                          ? ' fa-chevron-right'
                          : ' fa-chevron-left')
                      "
                    ></i>
                  </button>
                  <div class="w-5"></div>
                  <button :disabled="isLastPage" @click="nextPage">
                    <i
                      :class="
                        'fa-sharp fa-solid ' +
                        ($store.getters['isRtl']
                          ? ' fa-chevron-left'
                          : ' fa-chevron-right')
                      "
                    ></i>
                  </button>
                </template>
              </EasyDataTable>
            </div>
          </div>
        </div>
      </div>      <!-- Last Sale Invoices Section -->
      <div v-if="activeTab === 'dashboard'" class="px-6 py-6">
        <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3
              class="text-lg font-semibold text-gray-900 dark:text-white mb-4"
            >
              {{ $t("last_sale_invoices") }}
            </h3>
          <EasyDataTable
            :headers="headersLastSaleInvoices"
            :items="lastSaleInvoices"
            :body-text-direction="$store.getters['isRtl'] ? 'right' : 'left'"
           
            :header-props="{
              'sort-icon': 'fa-solid fa-arrow-up-wide-short',
            }"
            :loading="isLoadingData"
            :class="getTableClass()"
            :table-class-name="getTableClassName()"
            :key="`last-invoices-table-${isDark ? 'dark' : 'light'}`"
            :hide-footer="true"
          >
            <template #default="{ column, sortDirection }">
              <span class="flex items-center font-semibold text-gray-700 dark:text-gray-300">
                <span class="custom-sort-icon mr-2" v-if="column.sortable">
                  <i
                    v-if="sortDirection === 'asc'"
                    class="fa-solid fa-arrow-up text-blue-500"
                  ></i>
                  <i
                    v-else-if="sortDirection === 'desc'"
                    class="fa-solid fa-arrow-down text-blue-500"
                  ></i>
                  <i
                    v-else
                    class="fa-solid fa-arrows-up-down text-gray-400"
                  ></i>
                </span>
                {{ column.label }}
              </span>
            </template>

            <template #item-actions="slotProps">  
              <div class="flex items-center justify-center space-x-1">
                <!-- View Button -->
                <a 
                  class="inline-flex items-center justify-center p-2 text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-800/50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 rounded-md transition-all duration-200"
                  :href="'../sale-pos/' + slotProps.in_id"
                  :title="$t('view_invoice')"
                >
                  <i class="fa-solid fa-eye text-lg"></i>
                </a>
                
                <!-- Print Button -->
                <button
                  class="inline-flex items-center justify-center p-2 text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-800/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  @click="printA4Direct(slotProps.in_id)"
                  :title="$t('print_invoice')"
                  :disabled="printingInvoiceId === slotProps.in_id && isPrintLoading"
                >
                  <i v-if="printingInvoiceId === slotProps.in_id && isPrintLoading" class="fa-solid fa-spinner fa-spin text-lg"></i>
                  <i v-else class="fa-solid fa-print text-lg"></i>
                </button>
              </div>
            </template>
            <template #loading>
              <img
                src="../../assets/loading.webp"
                style="width: 70px; height: 70px"
              />
            </template>

            <template #empty-message>
              <span class="text-textBlack1 dark:text-white">
                {{ $t("no_data_found") }}
              </span>
            </template>

            <template v-slot:[`item-customer.cu_name`]="slotProps">
              <span class="text-gray-900 dark:text-white">
                {{ slotProps.customer ? slotProps.customer.cu_name : '-' }}
              </span>
            </template>

            <template v-slot:[`item-sale_type.st_name`]="slotProps">
              <span class="text-gray-900 dark:text-white">
                {{ slotProps.sale_type ? slotProps.sale_type.st_name : '-' }}
              </span>
            </template>

            <template v-slot:[`item-in_total_price`]="slotProps">
              <span class="text-green-600 dark:text-green-400 font-medium">
                {{ formatNumber(slotProps.in_price) }} {{ slotProps.currency ? $t(slotProps.currency.cur_name) : '' }}
              </span>
            </template>

            <template v-slot:[`item-in_total_discount`]="slotProps">
              <span class="text-red-600 dark:text-red-400 font-medium">
                {{ formatNumber(slotProps.in_discount) }} {{ slotProps.currency ? $t(slotProps.currency.cur_name) : '' }}
              </span>
            </template>
            <template v-slot:[`item-in_paid`]="slotProps">
              <span class="text-green-600 dark:text-green-400 font-medium">
                {{ formatNumber(slotProps.in_paid) }} {{ slotProps.currency ? $t(slotProps.currency.cur_name) : '' }}
              </span>
            </template>
            <template v-slot:[`item-in_due`]="slotProps">
              <span class="text-red-600 dark:text-red-400 font-medium">
                {{ formatNumber(slotProps.in_due) }} {{ slotProps.currency ? $t(slotProps.currency.cur_name) : '' }}
              </span>
            </template>
            <template v-slot:[`item-in_total_profit`]="slotProps">
              <span class="text-blue-600 dark:text-blue-400 font-medium">
                {{ formatNumber(slotProps.in_price) }} {{ slotProps.currency ? $t(slotProps.currency.cur_name) : '' }}
              </span>
            </template>

            <template v-slot:[`item-created_at`]="slotProps">
              <span class="text-gray-600 dark:text-gray-400">
                {{ formatDate(slotProps.created_at) }}
              </span>
            </template>

            <template v-slot:[`item-users.name`]="slotProps">
              <span class="text-gray-900 dark:text-white">
                {{ slotProps.users ? slotProps.users.name : '-' }}
              </span>
            </template>
          </EasyDataTable>
        </div>
      </div>

      <!-- Debt Limit Exceeded Customers Section -->
      <div v-if="activeTab === 'dashboard'" class="px-6 py-6">
        <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {{ $t("debt_limit_exceeded_customers") }}
          </h3>
          <EasyDataTable
            :headers="headersDebtLimitExceeded"
            :items="debtLimitExceededCustomers"
            :body-text-direction="$store.getters['isRtl'] ? 'right' : 'left'"
            alternating
            :header-props="{
              'sort-icon': 'fa-solid fa-arrow-up-wide-short',
            }"
            :loading="isLoadingData"
            :class="getTableClass()"
            :table-class-name="getTableClassName()"
            :key="`debt-limit-table-${isDark ? 'dark' : 'light'}`"
            :hide-footer="true"
          >
            <template #default="{ column, sortDirection }">
              <span class="flex items-center font-semibold text-gray-700 dark:text-gray-300">
                <span class="custom-sort-icon mr-2" v-if="column.sortable">
                  <i
                    v-if="sortDirection === 'asc'"
                    class="fa-solid fa-arrow-up text-blue-500"
                  ></i>
                  <i
                    v-else-if="sortDirection === 'desc'"
                    class="fa-solid fa-arrow-down text-blue-500"
                  ></i>
                  <i
                    v-else
                    class="fa-solid fa-arrows-up-down text-gray-400"
                  ></i>
                </span>
                {{ column.label }}
              </span>
            </template>
          
            <template #item-actions="item">
              <a :href="'customer-account/' + item.cu_id">
                <i class="fa-solid fa-eye text-blue-500"></i>
              </a>
            </template>
            <template #loading>
              <img
                src="../../assets/loading.webp"
                style="width: 70px; height: 70px"
              />
            </template>

            <template #item-cu_name="item">
              <span class="text-gray-900 dark:text-white font-medium">
                {{ item.cu_name }}
              </span>
            </template>

            <template #item-cu_debt_limit_dinar="item">
              <span v-if="item.cu_debt_limit_dinar" class="text-blue-600 dark:text-blue-400 font-semibold">
                {{ formatNumber(item.cu_debt_limit_dinar) }} {{ $t("dinar") }}
              </span>
              <span v-else class="text-gray-400">
                {{ $t("no_limit") }}
              </span>
            </template>

            <template #item-cu_debt_limit_dollar="item">
              <span v-if="item.cu_debt_limit_dollar" class="text-blue-600 dark:text-blue-400 font-semibold">
                {{ formatNumber(item.cu_debt_limit_dollar) }} $
              </span>
              <span v-else class="text-gray-400">
                {{ $t("no_limit") }}
              </span>
            </template>

            <template #item-current_debt_dinar="item">
              <span class="text-gray-900 dark:text-white">
                {{ formatNumber(item.current_debt_dinar) }} {{ $t("dinar") }}
              </span>
            </template>

            <template #item-current_debt_dollar="item">
              <span class="text-gray-900 dark:text-white">
                {{ formatNumber(item.current_debt_dollar) }} $
              </span>
            </template>

            <template #item-dinar_excess_amount="item">
              <span v-if="item.exceeds_dinar_limit" class="text-red-600 dark:text-red-400 font-semibold">
                {{ formatNumber(item.dinar_excess_amount) }} {{ $t("dinar") }}
              </span>
              <span v-else class="text-green-600 dark:text-green-400">
                -
              </span>
            </template>

            <template #item-dollar_excess_amount="item">
              <span v-if="item.exceeds_dollar_limit" class="text-red-600 dark:text-red-400 font-semibold">
                {{ formatNumber(item.dollar_excess_amount) }} $
              </span>
              <span v-else class="text-green-600 dark:text-green-400">
                -
              </span>
            </template>

            <template #empty-message>
              <span class="text-textBlack1 dark:text-white">
                {{ $t("no_data_found") }}
              </span>
            </template>
          </EasyDataTable>
        </div>
      </div>

      <!-- Tables Section -->
      <div v-if="activeTab === 'dashboard'" class="px-6 py-6">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Notes Table -->
          <div
            class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
          >
            <h3
              class="text-lg font-semibold text-gray-900 dark:text-white mb-4"
            >
              {{ $t("notes") }}
            </h3>
            <EasyDataTable
              :headers="headersNote"
              :items="items.notes"
              :body-text-direction="$store.getters['isRtl'] ? 'right' : 'left'"
             
              :header-props="{
                'sort-icon': 'fa-solid fa-arrow-up-wide-short',
              }"
              :loading="isLoadingData"
              :class="getTableClass()"
              :table-class-name="getTableClassName()"
              :key="`table-${isDark ? 'dark' : 'light'}`"
              :hide-footer="true"
            >
              <template #default="{ column, sortDirection }">
                <span
                  class="flex items-center font-semibold text-gray-700 dark:text-gray-300"
                >
                  <span class="custom-sort-icon mr-2" v-if="column.sortable">
                    <i
                      v-if="sortDirection === 'asc'"
                      class="fa-solid fa-arrow-up text-blue-500"
                    ></i>
                    <i
                      v-else-if="sortDirection === 'desc'"
                      class="fa-solid fa-arrow-down text-blue-500"
                    ></i>
                    <i
                      v-else
                      class="fa-solid fa-arrows-up-down text-gray-400"
                    ></i>
                  </span>
                  {{ column.label }}
                </span>
              </template>

              <template #loading>
                <img
                  src="../../assets/loading.webp"
                  style="width: 70px; height: 70px"
                />
              </template>

              <template #empty-message>
                <span class="text-textBlack1 dark:text-white">
                  {{ $t("no_data_found") }}
                </span>
              </template>
              <template
                #pagination="{ prevPage, nextPage, isFirstPage, isLastPage }"
              >
                <button :disabled="isFirstPage" @click="prevPage">
                  <i
                    :class="
                      'fa-sharp fa-solid ' +
                      ($store.getters['isRtl']
                        ? ' fa-chevron-right'
                        : ' fa-chevron-left')
                    "
                  ></i>
                </button>
                <div class="w-5"></div>
                <button :disabled="isLastPage" @click="nextPage">
                  <i
                    :class="
                      'fa-sharp fa-solid ' +
                      ($store.getters['isRtl']
                        ? ' fa-chevron-left'
                        : ' fa-chevron-right')
                    "
                  ></i>
                </button>
              </template>
            </EasyDataTable>
          </div>

          <!-- Waste Items Table -->
          <div
            class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
          >
            <h3
              class="text-lg font-semibold text-gray-900 dark:text-white mb-4"
            >
              {{ $t("waste_items") }}
            </h3>
            <EasyDataTable
              :headers="headerWasteItem"
              :items="items.waste_item"
              :body-text-direction="$store.getters['isRtl'] ? 'right' : 'left'"
              
              alternating
              :header-props="{
                'sort-icon': 'fa-solid fa-arrow-up-wide-short',
              }"
              :loading="isLoadingData"
              :class="getTableClass()"
              :table-class-name="getTableClassName()"
              :key="`table-${isDark ? 'dark' : 'light'}`"
              :hide-footer="true"
            >
              <template #default="{ column, sortDirection }">
                <span
                  class="flex items-center font-semibold text-gray-700 dark:text-gray-300"
                >
                  <span class="custom-sort-icon mr-2" v-if="column.sortable">
                    <i
                      v-if="sortDirection === 'asc'"
                      class="fa-solid fa-arrow-up text-blue-500"
                    ></i>
                    <i
                      v-else-if="sortDirection === 'desc'"
                      class="fa-solid fa-arrow-down text-blue-500"
                    ></i>
                    <i
                      v-else
                      class="fa-solid fa-arrows-up-down text-gray-400"
                    ></i>
                  </span>
                  {{ column.label }}
                </span>
              </template>

              <template #loading>
                <img
                  src="../../assets/loading.webp"
                  style="width: 70px; height: 70px"
                />
              </template>
              <template #item-total_cost_price_dollar="item">
                <span>
                  {{ formatNumber(item.total_cost_price_dollar) }}
                </span>
              </template>
              <template #item-total_cost_price_dinar="item">
                <span>
                  {{ formatNumber(item.total_cost_price_dinar) }}
                </span>
              </template>

              <template #empty-message>
                <span class="text-textBlack1 dark:text-white">
                  {{ $t("no_data_found") }}
                </span>
              </template>
              <template
                #pagination="{ prevPage, nextPage, isFirstPage, isLastPage }"
              >
                <button :disabled="isFirstPage" @click="prevPage">
                  <i
                    :class="
                      'fa-sharp fa-solid ' +
                      ($store.getters['isRtl']
                        ? ' fa-chevron-right'
                        : ' fa-chevron-left')
                    "
                  ></i>
                </button>
                <div class="w-5"></div>
                <button :disabled="isLastPage" @click="nextPage">
                  <i
                    :class="
                      'fa-sharp fa-solid ' +
                      ($store.getters['isRtl']
                        ? ' fa-chevron-left'
                        : ' fa-chevron-right')
                    "
                  ></i>
                </button>
              </template>
            </EasyDataTable>
          </div>
        </div>
      </div>


      <!-- Category Charts Section -->
      <div v-if="activeTab === 'dashboard'" class="px-6 py-6">
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- Income Categories Chart -->
          <div
            class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
          >
            <h3
              class="text-lg font-semibold text-gray-900 dark:text-white mb-4"
            >
              {{ $t("incomes_categorys") }}
            </h3>
            <div class="chart-container">
              <div class="text-center">
                <apex-chart
                  width="300"
                  type="donut"
                  :options="chartOptionsIncomes"
                  :series="seriesIncomes"
                  v-if="seriesIncomes.length > 0"
                ></apex-chart>
                <div
                  v-if="seriesIncomes.length == 0 && !isLoadingData"
                  class="py-8"
                >
                  <p class="text-gray-500 dark:text-gray-400">
                    {{ $t("no_data_found") }}
                  </p>
                </div>
                <div
                  v-if="seriesIncomes.length == 0 && isLoadingData"
                  class="py-8"
                >
                  <img
                    src="../../assets/loading.webp"
                    style="width: 70px; height: 70px"
                    class="mx-auto"
                  />
                </div>
              </div>
            </div>
          </div>
          <!-- Expense Categories Chart -->
          <div
            class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
          >
            <h3
              class="text-lg font-semibold text-gray-900 dark:text-white mb-4"
            >
              {{ $t("expenses_categorys") }}
            </h3>
            <div class="chart-container">
              <div class="items-center justify-center text-center">
                <apex-chart
                  width="300"
                  type="donut"
                  :options="chartOptionsExpenses"
                  :series="seriesExpenses"
                  v-if="seriesExpenses.length > 0"
                ></apex-chart>

                <div
                  v-if="seriesExpenses.length == 0 && !isLoadingData"
                  class="py-8"
                >
                  <p class="text-gray-500 dark:text-gray-400">
                    {{ $t("no_data_found") }}
                  </p>
                </div>
                <div
                  v-if="seriesExpenses.length == 0 && isLoadingData"
                  class="py-8"
                >
                  <img
                    src="../../assets/loading.webp"
                    style="width: 70px; height: 70px"
                    class="mx-auto"
                  />
                </div>
              </div>
            </div>
          </div>
          <!-- Debts Table -->
          <div
            class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
          >
            <h3
              class="text-lg font-semibold text-gray-900 dark:text-white mb-4"
            >
              {{ $t("debts") }}
            </h3>
            <EasyDataTable
              :headers="headersDebt"
              :items="items.debt"
              :body-text-direction="$store.getters['isRtl'] ? 'right' : 'left'"
              
              alternating
              :header-props="{
                'sort-icon': 'fa-solid fa-arrow-up-wide-short',
              }"
              :loading="isLoadingData"
              :class="getTableClass()"
              :table-class-name="getTableClassName()"
              :key="`table-${isDark ? 'dark' : 'light'}`"
              :hide-footer="true"
            >
              <template #default="{ column, sortDirection }">
                <span
                  class="flex items-center font-semibold text-gray-700 dark:text-gray-300"
                >
                  <span class="custom-sort-icon mr-2" v-if="column.sortable">
                    <i
                      v-if="sortDirection === 'asc'"
                      class="fa-solid fa-arrow-up text-blue-500"
                    ></i>
                    <i
                      v-else-if="sortDirection === 'desc'"
                      class="fa-solid fa-arrow-down text-blue-500"
                    ></i>
                    <i
                      v-else
                      class="fa-solid fa-arrows-up-down text-gray-400"
                    ></i>
                  </span>
                  {{ column.label }}
                </span>
              </template>

              <template #loading>
                <img
                  src="../../assets/loading.webp"
                  style="width: 70px; height: 70px"
                />
              </template>
              <template #item-total_debt_number="item">
                <span
                  :class="
                    selectedReport == 'total_debt_number'
                      ? 'text-primaryColor font-bold'
                      : ''
                  "
                >
                  {{ formatNumber(item.total_debt_number) }}
                </span>
              </template>
              <template #item-total_amount_dinar="item">
                <span
                  :class="
                    selectedReport == 'total_amount_dinar'
                      ? 'text-primaryColor font-bold'
                      : ''
                  "
                >
                  {{ formatNumber(item.total_amount_dinar) }}
                </span>
              </template>

              <template #item-total_amount_dollar="item">
                <span
                  :class="
                    selectedReport == 'total_amount_dollar'
                      ? 'text-primaryColor font-bold'
                      : ''
                  "
                >
                  {{ formatNumber(item.total_amount_dollar) }}
                </span>
              </template>
              <template #item-name="item">
                <span
                  :class="
                    item.name == 'invoice' ? 'text-red-500 font-bold' : ''
                  "
                  v-show="item.name == 'invoice'"
                >
                  {{ $t("sale_due") }}
                </span>
                <span
                  :class="
                    item.name == 'purchase' ? 'text-green-500 font-bold' : ''
                  "
                  v-show="item.name == 'purchase'"
                >
                  {{ $t("purchase_due") }}
                </span>
                <span
                  :class="
                    item.name == 'borrow' ? 'text-green-500 font-bold' : ''
                  "
                  v-show="item.name == 'borrow'"
                >
                  {{ $t("borrow") }}
                </span>
                <span
                  :class="item.name == 'lend' ? 'text-red-500 font-bold' : ''"
                  v-show="item.name == 'lend'"
                >
                  {{ $t("lend") }}
                </span>
                <span
                  :class="
                    item.name == 'returned_purchase'
                      ? 'text-red-500 font-bold'
                      : ''
                  "
                  v-show="item.name == 'returned_purchase'"
                >
                  {{ $t("returned_purchase") }}
                </span>
              </template>
              <template #empty-message>
                <span class="text-textBlack1 dark:text-white">
                  {{ $t("no_data_found") }}
                </span>
              </template>
              <template
                #pagination="{ prevPage, nextPage, isFirstPage, isLastPage }"
              >
                <button :disabled="isFirstPage" @click="prevPage">
                  <i
                    :class="
                      'fa-sharp fa-solid ' +
                      ($store.getters['isRtl']
                        ? ' fa-chevron-right'
                        : ' fa-chevron-left')
                    "
                  ></i>
                </button>
                <div class="w-5"></div>
                <button :disabled="isLastPage" @click="nextPage">
                  <i
                    :class="
                      'fa-sharp fa-solid ' +
                      ($store.getters['isRtl']
                        ? ' fa-chevron-left'
                        : ' fa-chevron-right')
                    "
                  ></i>
                </button>
              </template>
            </EasyDataTable>
          </div>
        </div>
      </div>
    </div>

    <!-- Pages Tab - Card-based UI -->
    <div v-if="activeTab === 'pages'" class="px-6 py-6">
      <!--<div class="mb-6">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {{ $t("application_pages") }}
        </h2>
        <p class="text-gray-600 dark:text-gray-400">
          {{ $t("access_all_features") }}
        </p>
      </div>-->

      <!-- Cards Grid -->
      <div
        class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6"
      >
        <!-- Dynamic Cards from Sidebar Items -->
        <div
          v-for="page in filteredPageItems"
          :key="page.id"
          @click="navigateToPage(page.href)"
          :class="[
            'group bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 cursor-pointer hover:shadow-lg transition-all duration-200',
            getHoverBorderClass(page.color),
          ]"
        >
          <div class="flex flex-col items-center text-center">
            <div
              :class="[
                'w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-colors',
                getIconBgClass(page.color),
                getIconHoverBgClass(page.color),
              ]"
            >
              <i
                :class="[
                  page.icon,
                  getIconTextClass(page.color),
                ]"
              ></i>
            </div>
            <h3
              class="text-sm font-medium text-gray-900 dark:text-white mb-1"
            >
              {{ page.name }}
            </h3>
            <p class="text-xs text-gray-500 dark:text-gray-400">
              {{ page.description }}
            </p>
          </div>
        </div>
      </div>

    </div>
  </div>
  <LoadingProgress v-show="isLoadingData" />

  <!-- Custom Date Range Dialog -->
  <div
    v-if="showDateRangeDialog"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    @click.self="cancelDateRangeDialog"
  >
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
          {{ $t("custom_date_range") }}
        </h3>
        <button
          @click="cancelDateRangeDialog"
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <i class="fa-solid fa-times text-xl"></i>
        </button>
      </div>

      <div class="space-y-4">
        <!-- Start Date -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {{ $t("start_date") }}
          </label>
          <input
            type="date"
            v-model="customStartDate"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          />
        </div>

        <!-- End Date -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {{ $t("end_date") }}
          </label>
          <input
            type="date"
            v-model="customEndDate"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex justify-end space-x-3 mt-6">
        <button
          @click="cancelDateRangeDialog"
          class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
        >
          {{ $t("cancel") }}
        </button>
        <button
          @click="applyCustomDateRange"
          :disabled="!customStartDate || !customEndDate"
          class="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors"
        >
          {{ $t("apply") }}
        </button>
      </div>
    </div>
  </div>

  <!-- Print Invoice Modal -->
  <div
    v-if="showPrintModal"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    @click.self="closePrintModal"
  >
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
          {{ $t("print_invoice") }}
        </h3>
        <button
          @click="closePrintModal"
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <i class="fa-solid fa-times text-xl"></i>
        </button>
      </div>

             <div class="space-y-4">
               <p class="text-sm text-gray-600 dark:text-gray-400">
                 {{ $t("select_print_format") }}
               </p>

               <!-- Print Options -->
               <div class="space-y-3">
                 <!-- A4 Print Option -->
                 <button
                   @click="printA4"
                   :disabled="isPrintLoading"
                   class="w-full flex items-center justify-center space-x-3 p-4 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                   <div class="flex items-center space-x-3">
                     <i class="fa-solid fa-file-invoice text-green-500 text-xl"></i>
                     <div class="text-left">
                       <div class="font-medium text-gray-900 dark:text-white">
                         {{ $t("a4_invoice") }}
                       </div>
                       <div class="text-sm text-gray-500 dark:text-gray-400">
                         {{ $t("detailed_invoice_format") }}
                       </div>
                     </div>
                   </div>
                   <i class="fa-solid fa-chevron-right text-gray-400"></i>
                 </button>
               </div>

        <!-- Loading State -->
        <div v-if="isPrintLoading" class="flex items-center justify-center py-4">
          <div class="flex items-center space-x-2">
            <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
            <span class="text-sm text-gray-600 dark:text-gray-400">
              {{ $t("loading") }}...
            </span>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex justify-end mt-6">
        <button
          @click="closePrintModal"
          :disabled="isPrintLoading"
          class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50"
        >
          {{ $t("cancel") }}
        </button>
      </div>
    </div>
  </div>
</template>
<script>
//import hearder-app
import HeaderApp from "../../components/Navbar/header-app.vue";
import ReportHomeCard from "../../components/Report/ReportHomeCard.vue";
import moment from "moment";
import { formatDate } from "../../utils/functions";
import { formatNumber } from "../../utils/functions";
import { FILE_URL } from "../../constants/config";
import LoadingProgress from "../../components/loading-progress.vue";
export default {
  name: "HelloWorld",
  components: {
    HeaderApp,
    ReportHomeCard,
    LoadingProgress,
  },
  data() {
    return {
      activeTab: "dashboard",
      showNumbers: localStorage.getItem('dashboard_show_numbers') !== 'false', // Toggle for showing/hiding numbers in cards
      showDateRangeDialog: false, // Show/hide date range dialog
      customStartDate: '', // Custom start date
      customEndDate: '', // Custom end date
      showPrintModal: false, // Show/hide print modal
      selectedInvoiceId: null, // Selected invoice ID for printing
      isPrintLoading: false, // Loading state for print
      file_url: FILE_URL,
      series: [],
      seriesItems: [],
      seriesIncomes: [],
      seriesExpenses: [],
      salesTrendData: [],

      selectedReport: "item_quantity_sold",
      chartOptions: {
        chart: {
          width: 380,
          type: "donut",
          fontFamily: "kurdish",
        },
        stroke: {
          width: 0,
        },

        labels: [],
        //change labels position to bottom
        legend: {
          position: "bottom",
          //labels colo dark and light
          labels: {
            colors: "#fff",
          },
        },
        dataLabels: {
          dropShadow: {
            blur: 3,
            opacity: 0.8,
          },
        },

        states: {
          hover: {
            filter: "none",
          },
        },

        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 300,
              },
              legend: {
                position: "bottom",
              },
            },
          },
        ],
      },
      chartOptionsItems: {
        chart: {
          width: 380,
          type: "donut",
          fontFamily: "kurdish",
        },
        stroke: {
          width: 0,
        },

        labels: [],
        //change labels position to bottom
        legend: {
          position: "bottom",
          //labels colo dark and light
          labels: {
            colors: "#fff",
          },
        },
        dataLabels: {
          dropShadow: {
            blur: 3,
            opacity: 0.8,
          },
        },

        states: {
          hover: {
            filter: "none",
          },
        },

        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 300,
              },
              legend: {
                position: "bottom",
              },
            },
          },
        ],
      },
      chartOptionsIncomes: {
        chart: {
          width: 380,
          type: "donut",
          fontFamily: "kurdish",
        },
        stroke: {
          width: 0,
        },

        labels: [],
        //change labels position to bottom
        legend: {
          position: "bottom",
          //labels colo dark and light
          labels: {
            colors: "#fff",
          },
        },
        dataLabels: {
          dropShadow: {
            blur: 3,
            opacity: 0.8,
          },
        },

        states: {
          hover: {
            filter: "none",
          },
        },

        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 300,
              },
              legend: {
                position: "bottom",
              },
            },
          },
        ],
      },
      chartOptionsExpenses: {
        chart: {
          width: 380,
          type: "donut",
          fontFamily: "kurdish",
        },
        stroke: {
          width: 0,
        },

        labels: [],
        //change labels position to bottom
        legend: {
          position: "bottom",
          //labels colo dark and light
          labels: {
            colors: "#fff",
          },
        },
        dataLabels: {
          dropShadow: {
            blur: 3,
            opacity: 0.8,
          },
        },

        states: {
          hover: {
            filter: "none",
          },
        },

        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 300,
              },
              legend: {
                position: "bottom",
              },
            },
          },
        ],
      },
      // Sales Trend Chart Options
      salesTrendChartOptions: {
        chart: {
          type: 'line',
          height: 380,
          fontFamily: 'kurdish',
          background: 'transparent',
          toolbar: {
            show: false
          },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800
          }
        },
        stroke: {
          curve: 'smooth',
          width: 3,
          lineCap: 'round'
        },
        colors: ['#3b82f6', '#10b981'],
        markers: {
          size: 0,
          strokeWidth: 0,
          hover: {
            size: 0
          }
        },
        xaxis: {
          type: 'category',
          labels: {
            style: {
              colors: '#374151',
              fontSize: '12px',
              fontFamily: 'kurdish'
            },
            rotate: 0
          },
          axisBorder: {
            show: false
          },
          axisTicks: {
            show: false
          }
        },
        yaxis: {
          labels: {
            style: {
              colors: '#374151',
              fontSize: '12px',
              fontFamily: 'kurdish'
            },
            formatter: function (val) {
              if (val >= 1000000) {
                return (val / 1000000).toFixed(1) + 'M';
              } else if (val >= 1000) {
                return (val / 1000).toFixed(1) + 'K';
              }
              return val.toLocaleString();
            }
          },
          axisBorder: {
            show: false
          }
        },
        grid: {
          borderColor: '#e5e7eb',
          strokeDashArray: 0,
          xaxis: {
            lines: {
              show: true
            }
          },
          yaxis: {
            lines: {
              show: true
            }
          }
        },
        legend: {
          show: false
        },
        tooltip: {
          theme: 'light',
          shared: true,
          intersect: false,
          x: {
            formatter: function (val) {
              return val;
            }
          },
          y: {
            formatter: function (val, opts) {
              const seriesName = opts.series[opts.seriesIndex].name;
              const currency = seriesName.includes('Dollar') ? 'USD' : 'IQD';
              return val.toLocaleString() + ' ' + currency;
            }
          }
        },
        dataLabels: {
          enabled: false
        },
        fill: {
          type: 'solid',
          opacity: 0
        },
        responsive: [
          {
            breakpoint: 768,
            options: {
              chart: {
                height: 350
              }
            }
          }
        ]
      },

      rules: [
        (value) => {
          if (value) return true;

          return this.$t("you_must_fill_this_field");
        },
      ],
      inputType: "text",
      isSubmited: false,
      isSubmitedEdit: false,
      current: {},
      startDate: "",
      endDate: "",
      period: "last30days",
      iOweIqd: 0,
      iOweUsd: 0,
      theyOweIqd: 0,
      theyOweUsd: 0,
      printingInvoiceId: null, // Added state for printing
    };
  },
  computed: {
    isDark() {
      return this.$store.state.theme == "dark";
    },
    isLoadingData() {
      return this.$store.state.home.isLoadingData;
    },
    items() {
      return this.$store.state.home.items;
    },
    sidebarItems() {
      // Get sidebar items from Vuex store
      return this.$store.state.navbar.sidebarItems || [];
    },
    lastSaleInvoices() {
      return this.$store.state.home.items.invoices || [];
    },
    debtLimitExceededCustomers() {
      return this.$store.state.home.items.debt_limit_exceeded_customers || [];
    },
    salesTrend() {
      const trend = this.$store.state.home.items.sales_trend || {};
      console.log('salesTrend computed property:', trend);
      return trend;
    },
    allPageItems() {
      // Flatten all sidebar items and sub-items into a single array
      const items = [];
      const priorityItems = ["sale-pos", "new-purchase", "add-box", "item-list", "debt-list"];
      const priorityItemsList = [];
      const regularItems = [];

      // Add main items (excluding "report" parent and all its sub-items)
      this.sidebarItems.forEach((item) => {
        // Skip the "report" parent item and all its sub-items
        if (item.id === "report") {
          // Skip all report items
          return;
        }

        // Add other main items
        if (item.access) {
          const mainItem = {
            id: item.id,
            name: item.name,
            href: item.href,
            icon: item.icon,
            description: this.getPageDescription(item.id),
            color: this.getPageColor(item.id),
          };
          
          if (priorityItems.includes(item.id)) {
            priorityItemsList.push(mainItem);
          } else {
            regularItems.push(mainItem);
          }
        }

        // Add sub-items for other main items
        if (item.subItems && item.subItems.length > 0) {
          item.subItems.forEach((subItem) => {
            if (subItem.access) {
              const subItemObj = {
                id: subItem.id,
                name: subItem.text,
                href: subItem.href,
                icon: this.getSubItemIcon(subItem.id),
                description: this.getPageDescription(subItem.id),
                color: this.getPageColor(subItem.id),
              };
              
              if (priorityItems.includes(subItem.id)) {
                priorityItemsList.push(subItemObj);
              } else {
                regularItems.push(subItemObj);
              }
            }
          });
        }
      });
      
      // Combine priority items first, then regular items
      items.push(...priorityItemsList, ...regularItems);
      console.log(items);
      return items;
    },
    filteredPageItems() {
      // Filter out items with empty, null, or root href
      return this.allPageItems.filter(page => 
        page.href && page.href !== '' && page.href !== '/'
      );
    },
    headersSaleType() {
      return [
        { text: "#", value: "st_id", sortable: true },
        { text: this.$t("sale_type_name"), value: "st_name", sortable: true },
        {
          text: this.$t("sold_quantity"),
          value: "item_quantity_sold",
          sortable: true,
        },
        {
          text: this.$t("customer_count"),
          value: "invoice_count",
          sortable: true,
        },
        {
          text: this.$t("total_sale_dinar"),
          value: "total_sale_price_dinar",
          sortable: true,
        },
        {
          text: this.$t("total_cost_dinar"),
          value: "total_cost_price_dinar",
          sortable: true,
        },
        {
          text: this.$t("total_profit_dinar"),
          value: "total_profit_dinar",
          sortable: true,
        },
        {
          text: this.$t("total_discount_dinar"),
          value: "total_discount_dinar",
          sortable: true,
        },
        {
          text: this.$t("total_sale_dollar"),
          value: "total_sale_price_dollar",
          sortable: true,
        },
        {
          text: this.$t("total_cost_dollar"),
          value: "total_cost_price_dollar",
          sortable: true,
        },
        {
          text: this.$t("total_profit_dollar"),
          value: "total_profit_dollar",
          sortable: true,
        },
        {
          text: this.$t("total_discount_dollar"),
          value: "total_discount_dollar",
          sortable: true,
        },
      ];
    },
    headersMostSoldItem() {
      return [
        { text: "#", value: "i_id", sortable: true },
        { text: this.$t("item_name"), value: "i_name", sortable: true },
        {
          text: this.$t("sold_quantity"),
          value: "item_quantity_sold",
          sortable: true,
        },

        {
          text: this.$t("total_sale_dinar"),
          value: "total_sale_price_dinar",
          sortable: true,
        },

        {
          text: this.$t("total_profit_dinar"),
          value: "total_profit_dinar",
          sortable: true,
        },
        {
          text: this.$t("total_discount_dinar"),
          value: "total_discount_dinar",
          sortable: true,
        },
        {
          text: this.$t("total_sale_dollar"),
          value: "total_sale_price_dollar",
          sortable: true,
        },

        {
          text: this.$t("total_profit_dollar"),
          value: "total_profit_dollar",
          sortable: true,
        },
        {
          text: this.$t("total_discount_dollar"),
          value: "total_discount_dollar",
          sortable: true,
        },
      ];
    },
    headersNote() {
      return [
        { text: "#", value: "no_id", sortable: true },
        { text: this.$t("note_title"), value: "no_title", sortable: true },
        {
          text: this.$t("note_description"),
          value: "no_description",
          sortable: true,
        },
        { text: this.$t("admin"), value: "name", sortable: true },
        { text: this.$t("created_at"), value: "created_at", sortable: true },
      ];
    },
    headerWasteItem() {
      return [
        { text: "#", value: "i_id", sortable: true },
        {
          text: this.$t("wasted_item_name"),
          value: "i_name",
          sortable: true,
        },
        {
          text: this.$t("wasted_quantity"),
          value: "total_waste_quantity",
          sortable: true,
        },

        {
          text: this.$t("total_loss_in_dinar"),
          value: "total_cost_price_dinar",
          sortable: true,
        },
        {
          text: this.$t("total_loss_in_dollar"),
          value: "total_cost_price_dollar",
          sortable: true,
        },
      ];
    },
    headersDebt() {
      return [
        { text: this.$t("debt_type"), value: "name", sortable: true },
        {
          text: this.$t("debts_count"),
          value: "total_debt_number",
          sortable: true,
        },
        {
          text: this.$t("total_debt_dinar"),
          value: "total_amount_dinar",
          sortable: true,
        },
        {
          text: this.$t("total_debt_dollar"),
          value: "total_amount_dollar",
          sortable: true,
        },
      ];
    },
    headersLastSaleInvoices() {
      return [
        //action
        { text: this.$t("actions"), value: "actions", sortable: false },
        { text: "#", value: "in_id", sortable: true },
        { text: this.$t("customer"), value: "customer.cu_name", sortable: true },
        { text: this.$t("sale_type"), value: "sale_type.st_name", sortable: true },
        { text: this.$t("total_price"), value: "in_total_price", sortable: true },
        { text: this.$t("total_discount"), value: "in_total_discount", sortable: true },
        //paid and debt balance
        { text: this.$t("paid_amount"), value: "in_paid", sortable: true },
        { text: this.$t("debt"), value: "in_due", sortable: true },
        { text: this.$t("created_at"), value: "created_at", sortable: true },
        { text: this.$t("Users"), value: "users.name", sortable: true },

      ];
    },
    headersDebtLimitExceeded() {
      return [
        { text: "#", value: "cu_id", sortable: true },
        { text: this.$t("customer_name"), value: "cu_name", sortable: true },
        { text: this.$t("debt_limit_dinar"), value: "cu_debt_limit_dinar", sortable: true },
        { text: this.$t("debt_limit_dollar"), value: "cu_debt_limit_dollar", sortable: true },
        { text: this.$t("current_debt_dinar"), value: "current_debt_dinar", sortable: true },
        { text: this.$t("current_debt_dollar"), value: "current_debt_dollar", sortable: true },
        //actions
        { text: this.$t("actions"), value: "actions", sortable: false },
      ];
    },
  },
  watch: {
    salesTrend: {
      handler() {
        this.processSalesTrendData();
      },
      deep: true,
      immediate: true
    }
  },
  async created() {
    // Load units and items to ensure unit information is available
    this.$store.dispatch("item_unit/getUnits");
    this.$store.dispatch("navbar/getItems");
    /*console.log(await this.$store.dispatch("checkUserAccess", {
      id: "1",
      action: "view",
    }));*/
    /* console.log(
      this.$store.getters["checkUserAccess"]({
        id: "1",
        action: "view",
      })
    );*/
    this.startDate = moment().subtract(30, "days").format("YYYY-MM-DD");
    this.endDate = moment().format("YYYY-MM-DD");
    this.getData(0); //change labelscolor dark and light
    if (this.isDark) {
      this.chartOptions.legend.labels.colors = "#fff";
      this.chartOptionsItems.legend.labels.colors = "#fff";
      this.chartOptionsIncomes.legend.labels.colors = "#fff";
      this.chartOptionsExpenses.legend.labels.colors = "#fff";
      // Update sales trend chart for dark mode
      this.salesTrendChartOptions.xaxis.labels.style.colors = "#e5e7eb";
      this.salesTrendChartOptions.yaxis.labels.style.colors = "#e5e7eb";
      this.salesTrendChartOptions.grid.borderColor = "#374151";
      this.salesTrendChartOptions.tooltip.theme = "dark";
    } else {
      this.chartOptions.legend.labels.colors = "#000";
      this.chartOptionsItems.legend.labels.colors = "#000";
      this.chartOptionsIncomes.legend.labels.colors = "#000";
      this.chartOptionsExpenses.legend.labels.colors = "#000";
      // Update sales trend chart for light mode
      this.salesTrendChartOptions.xaxis.labels.style.colors = "#374151";
      this.salesTrendChartOptions.yaxis.labels.style.colors = "#374151";
      this.salesTrendChartOptions.grid.borderColor = "#e5e7eb";
      this.salesTrendChartOptions.tooltip.theme = "light";
    }
    this.getDebts();
    
  },
  methods: {
    toggleNumbersVisibility() {
      this.showNumbers = !this.showNumbers;
      // Save state to localStorage
      localStorage.setItem('dashboard_show_numbers', this.showNumbers.toString());
    },
    applyCustomDateRange() {
      if (this.customStartDate && this.customEndDate) {
        // Set period to custom and trigger the report update
        this.period = 'custom';
        this.showDateRangeDialog = false;
        // Call the existing periodChanged method with custom dates
        this.periodChanged('custom', this.customStartDate, this.customEndDate);
      }
    },
    cancelDateRangeDialog() {
      this.showDateRangeDialog = false;
      this.customStartDate = '';
      this.customEndDate = '';
    },
    printInvoice(invoiceId) {
      this.selectedInvoiceId = invoiceId;
      this.showPrintModal = true;
    },
    async printA4Direct(invoiceId) {
      this.isPrintLoading = true;
      this.printingInvoiceId = invoiceId;
      try {
        // Find the invoice data
        const invoice = this.lastSaleInvoices.find(inv => inv.in_id === invoiceId);
        if (!invoice) {
          console.error('Invoice not found');
          this.isPrintLoading = false;
          this.printingInvoiceId = null;
          return;
        }

        // Get customer debt balance if customer exists
        let beforeDebtIQD = 0;
        let beforeDebtUSD = 0;
        let balanceUpToAndIncludingDateIQD = 0;
        let balanceUpToAndIncludingDateUSD = 0;

        if (invoice.customer && invoice.customer.cu_id) {
          try {
            const res = await this.$store.dispatch("customer/getCurrentDebt", {
              cu_id: invoice.customer.cu_id,
              created_at: invoice.created_at,
            });
            beforeDebtIQD = res.balance_before_date.IQD;
            beforeDebtUSD = res.balance_before_date.USD;
            balanceUpToAndIncludingDateIQD = res.balance_up_to_and_including_date.IQD;
            balanceUpToAndIncludingDateUSD = res.balance_up_to_and_including_date.USD;
          } catch (error) {
            console.error('Error getting customer debt:', error);
          }
        }

        // Create A4 invoice content
        const invoiceContent = this.generateA4Invoice(invoice, beforeDebtIQD, beforeDebtUSD, balanceUpToAndIncludingDateIQD, balanceUpToAndIncludingDateUSD);
        
        // Print the invoice
        this.printContent(invoiceContent);
        
        this.closePrintModal();
      } catch (error) {
        console.error('Error printing A4 invoice:', error);
        this.isPrintLoading = false;
      } finally {
        this.isPrintLoading = false;
        this.printingInvoiceId = null;
      }
    },
    closePrintModal() {
      this.showPrintModal = false;
      this.selectedInvoiceId = null;
      this.isPrintLoading = false;
    },
    async printA4() {
      if (!this.selectedInvoiceId) return;
      
      this.isPrintLoading = true;
      try {
        // Find the invoice data
        const invoice = this.lastSaleInvoices.find(inv => inv.in_id === this.selectedInvoiceId);
        if (!invoice) {
          console.error('Invoice not found');
          this.isPrintLoading = false;
          return;
        }

        // Get customer debt balance if customer exists
        let beforeDebtIQD = 0;
        let beforeDebtUSD = 0;
        let balanceUpToAndIncludingDateIQD = 0;
        let balanceUpToAndIncludingDateUSD = 0;

        if (invoice.customer && invoice.customer.cu_id) {
          try {
            const res = await this.$store.dispatch("customer/getCurrentDebt", {
              cu_id: invoice.customer.cu_id,
              created_at: invoice.created_at,
            });
            beforeDebtIQD = res.balance_before_date.IQD;
            beforeDebtUSD = res.balance_before_date.USD;
            balanceUpToAndIncludingDateIQD = res.balance_up_to_and_including_date.IQD;
            balanceUpToAndIncludingDateUSD = res.balance_up_to_and_including_date.USD;
          } catch (error) {
            console.error('Error getting customer debt:', error);
          }
        }

        // Create A4 invoice content
        const invoiceContent = this.generateA4Invoice(invoice, beforeDebtIQD, beforeDebtUSD, balanceUpToAndIncludingDateIQD, balanceUpToAndIncludingDateUSD);
        
        // Print the invoice
        this.printContent(invoiceContent);
        
        this.closePrintModal();
      } catch (error) {
        console.error('Error printing A4 invoice:', error);
        this.isPrintLoading = false;
      }
    },
    generateA4Invoice(invoice, beforeDebtIQD = 0, beforeDebtUSD = 0, balanceUpToAndIncludingDateIQD = 0, balanceUpToAndIncludingDateUSD = 0) {
      const settings = this.$store.state.settings.settings || {};
      const currency = invoice.currency || { cur_name: 'IQD' };
      const cur_id = currency.cur_id || 1;
      
      // Calculate totals from sold items
      const soldItems = invoice.sold_item || [];
      console.log('Invoice sold items:', soldItems);
      console.log('Invoice data:', invoice);
      const sumPriceWithoutDiscount = soldItems.reduce((sum, item) => sum + (item.si_quantity * item.si_sale_price), 0);
      const sumDiscount = soldItems.reduce((sum, item) => sum + (item.si_discount || 0), 0);
      const sumPrice = sumPriceWithoutDiscount - sumDiscount;
      const totalBonusItems = soldItems.reduce((sum, item) => sum + (item.si_bonus_quantity || 0), 0);
      
      return `
        <html>
        <head>
          <title>${this.$t("invoice_title")}</title>
          <style>
            @font-face {
              font-family: 'kurd';
              src: url('https://storage.wisesale.app/Rabar_022-Busi21Jx.ttf') format('truetype');
            }
            body {
              font-family: 'kurd', sans-serif;
              direction: rtl;
              text-align: right;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              background: white;
              margin: 0;
            }
            @page {
              size: A4;
            }
            .my_font { font-family: 'kurd' !important; }
            .invoice-container {
              max-width: 210mm;
              margin: 0 auto;
              box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
              padding: 10mm;
            }
            .header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding-bottom: 20px;
            }
            .headerBorder {
              border-bottom: 2px solid #eee;
            }
            .header h1 {
              font-size: 28px;
              color: #7165E3;
              margin: 0;
            }
            .header p {
              margin: 0;
              font-size: 12px;
              color: #777;
            }
            .header .company-info {
              text-align: right;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #eee;
              padding: 10px;
              text-align: center;
            }
            th {
              background-color: #7165E3;
              color: white;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .summary-box {
              margin-top: 20px;
              background: #f2f2f2;
              padding: 10px;
              border-radius: 5px;
            }
            .summary-box h3 {
              margin: 5px 0;
              color: #7165E3;
            }
            .summary-box1 h3 {
              margin: 1px 0;
              color: #7165E3;
            }
            .footer {
              margin-top: 40px;
              text-align: center;
              font-size: 14px;
            }
          </style>
        </head>
        <body>
          <div class="invoice-container">
            <div class="header headerBorder">
              <div class="company-info">
                <h1>${settings.app_name || 'Store'}</h1>
                <p>${this.$t("phone number")}: ${settings.set_phone_number || ''}${settings.set_phone_number2 ? ' - ' + settings.set_phone_number2 : ''}</p>
                <p>${this.$t("address")}: ${settings.set_address || ''}</p>
              </div>
              <div class="company-logo">
                ${settings.set_logo ? `<img src="${this.file_url + settings.set_logo}" alt="Company Logo" style="max-width: 100px; max-height: 100px;" />` : ''}
              </div>
              <div>
                <h1 style="color: #333; font-size: 20px;">${settings.set_header_invoice || ''}</h1>
                <p><strong>${this.$t("invoice_id")}:</strong> ${invoice.in_id || "-"}</p>
                <p><strong>${this.$t("date")}:</strong> ${this.formatDate(invoice.created_at)}</p>
              </div>
            </div>
            
            <div class="header">
              <h2 style="color: #333; font-size: 18px;">
                ${this.$t("customer_name")} : ${invoice.customer ? invoice.customer.cu_id + ' - ' + invoice.customer.cu_name : this.$t("walk_in_customer")}
              </h2>
              ${invoice.in_status != 1 ? `<p style="color: red; font-size: 16px; font-weight: bold;">${this.$t("returned")}</p>` : ''}
            </div>
            
            <table>
              <thead>
                <tr>
                  <th>${this.$t("#")}</th>
                  <th>${this.$t("item_name")}</th>
                  <th>${this.$t("price_dana")}</th>
                  <th>${this.$t("quantity")}</th>
                  ${totalBonusItems > 0 ? `<th>${this.$t("bonus")}</th>` : ''}
                  ${sumDiscount > 0 ? `<th>${this.$t("discount")}</th>` : ''}
                  <th>${this.$t("total_price")}</th>
                </tr>
              </thead>
              <tbody>
                ${soldItems.map((item, index) => `
                  <tr>
                    <td>${index + 1}</td>
                    <td>${item.item ? item.item.i_name : 'Unknown Item'}</td>
                    <td>${this.formatNumber(item.si_sale_price)}</td>
                    <td>${this.formatNumber(item.si_quantity)}</td>
                    ${totalBonusItems > 0 ? `<td>${item.si_bonus_quantity > 0 ? this.formatNumber(item.si_bonus_quantity) : '-'}</td>` : ''}
                    ${sumDiscount > 0 ? `<td>${this.formatNumber(item.si_discount || 0)}</td>` : ''}
                    <td>${this.formatNumber(item.si_quantity * item.si_sale_price - (item.si_discount || 0))}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            
            ${invoice.in_note ? `
              <div class="summary-box" style="margin: 10px 0 !important; padding: 8px !important;">
                <h3 style="margin: 0 0 5px 0 !important;">${this.$t("note")}</h3>
                <p style="margin: 0 !important;">${invoice.in_note}</p>
              </div>
            ` : ''}
            
            <div class="header">
              ${(beforeDebtIQD > 0 || beforeDebtIQD < 0 || beforeDebtUSD > 0 || beforeDebtUSD < 0 || balanceUpToAndIncludingDateIQD > 0 || balanceUpToAndIncludingDateIQD < 0 || balanceUpToAndIncludingDateUSD > 0 || balanceUpToAndIncludingDateUSD < 0) ? `
                <div style="display: flex; justify-content: flex-end; margin-top: 20px;">
                  <div style="background-color: #f2f2f2; padding: 15px; border-radius: 5px; border: 1px solid #ddd; box-sizing: border-box; min-width: 200px;">
                    <div style="padding: 4px 0;">
                      <div style="font-size: 14px;">
                        ${this.$t("balance_before")}: 
                        <div style="font-size: 14px; font-weight: bold; color: red;">
                          ${this.formatNumber(beforeDebtIQD)} ${this.$t("dinar")}
                        </div>
                      </div>
                      <div style="font-size: 14px; font-weight: bold; color: red;">
                        ${this.formatNumber(beforeDebtUSD)} ${this.$t("dollar")}
                      </div>
                    </div>
                    <div style="padding: 4px 0; margin-top: 10px;">
                      <div style="font-size: 14px;">
                        ${this.$t("balance_after")}:
                      </div>
                      <div style="font-size: 14px; font-weight: bold; color: red;">
                        ${this.formatNumber(balanceUpToAndIncludingDateIQD)} ${this.$t("dinar")}
                      </div>
                      <div style="font-size: 14px; font-weight: bold; color: red;">
                        ${this.formatNumber(balanceUpToAndIncludingDateUSD)} ${this.$t("dollar")}
                      </div>
                    </div>
                  </div>
                </div>
              ` : ''}
              
              <div style="display: flex; justify-content: flex-end; margin-top: 20px;">
                <div style="background-color: #f2f2f2; padding: 15px; border-radius: 5px; border: 1px solid #ddd; box-sizing: border-box;">
                  <div style="display: flex; justify-content: space-between; padding: 4px 0;">
                    <span style="font-size: 17px; font-weight: normal; color: #555;">${this.$t("items_price")}:</span>
                    <span style="font-size: 17px; font-weight: normal; color: #333;">${this.formatNumber(sumPriceWithoutDiscount)} ${this.$t(cur_id == 1 ? "dinar" : "dollar")}</span>
                  </div>
                  ${sumDiscount > 0 ? `
                    <div style="display: flex; justify-content: space-between; padding: 4px 0;">
                      <span style="font-size: 17px; font-weight: normal; color: #555;">${this.$t("discount")}:</span>
                      <span style="font-size: 17px; font-weight: normal; color: #333;">${this.formatNumber(sumDiscount)} ${this.$t(cur_id == 1 ? "dinar" : "dollar")}</span>
                    </div>
                  ` : ''}
                  <div style="display: flex; justify-content: space-between; padding: 4px 0; font-size: 18px; color: #333;">
                    <span style="color: #555;">${this.$t("final_price")}:</span>
                    <span style="font-weight: bold;">${this.formatNumber(sumPrice)} ${this.$t(cur_id == 1 ? "dinar" : "dollar")}</span>
                  </div>
                  ${invoice.in_due > 0 ? `
                    <div style="display: flex; justify-content: space-between; padding: 4px 0;">
                      <span style="font-size: 18px; font-weight: normal; color: #555;">${this.$t("paid_money")}:</span>
                      <span style="font-size: 18px; font-weight: bold; color: #008000;">${this.formatNumber(invoice.in_paid)} ${this.$t(cur_id == 1 ? "dinar" : "dollar")}</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; padding: 4px 0">
                      <span style="font-size: 18px; color: #555;">${this.$t("debt")}:</span>
                      <span style="font-size: 18px; font-weight: bold; color: red;">${this.formatNumber(invoice.in_due)} ${this.$t(cur_id == 1 ? "dinar" : "dollar")}</span>
                    </div>
                  ` : ''}
                </div>
              </div>
            </div>
           
          </div>
        </body>
        </html>
      `;
    },
    printContent(content) {
      // Create a hidden iframe for printing
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";

      document.body.appendChild(iframe);

      // Write content to iframe
      iframe.contentDocument.write(content);
      iframe.contentDocument.close();

      // Print after content is loaded
      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow.print();
          
          // Clean up
          setTimeout(() => {
            document.body.removeChild(iframe);
            this.isPrintLoading = false;
          }, 1000);
        }, 500);
      };
    },
    getTableClass() {
      return this.isDark ? "modern-dark-table" : "modern-light-table";
    },
    getTableClassName() {
      return this.isDark
        ? "modern-table dark-table"
        : "modern-table light-table";
    },
    getKurdishName(name) {
      if (name == "invoice") return this.$t("sale_due");
      else if (name == "purchase") return this.$t("purchase_due");
      else if (name == "borrow") return this.$t("borrow");
      else if (name == "lend") return this.$t("lend");
      else if (name == "returned_purchase") return this.$t("returned_purchase");
    },
    formatNumber(number) {
      return formatNumber(number);
    },
    formatDate(date) {
      return formatDate(date);
    },
    getItemUnitName(item) {
      // Try different possible data structures for unit
      if (item.unit && item.unit.un_name) {
        return item.unit.un_name;
      }
      if (item.un_name) {
        return item.un_name;
      }
      // Try to get from navbar items store if item has i_id
      if (item.i_id) {
        const navbarItems = this.$store.state.navbar.items || [];
        const fullItem = navbarItems.find(navItem => navItem.i_id === item.i_id);
        if (fullItem && fullItem.unit && fullItem.unit.un_name) {
          return fullItem.unit.un_name;
        }
      }
      // Try to get from item_unit store if item has un_id
      if (item.un_id) {
        const units = this.$store.state.item_unit.units || [];
        const unit = units.find(u => u.un_id === item.un_id);
        if (unit && unit.un_name) {
          return unit.un_name;
        }
      }
      return null;
    },
    async getData(num) {
      console.log('getData called with dates:', this.startDate, 'to', this.endDate);
      await this.$store.dispatch("home/getItems", {
        startDate: this.startDate,
        endDate: this.endDate,
        invoiceType: this.invoiceType,
      });
      if (num == 0) {
        this.changeReportType();

        //wait 3 min
        setTimeout(() => {
          this.changeReportType();
        }, 100);
      } else {
        this.changeReportType();
      }
      
      // Process sales trend data
      this.processSalesTrendData();
    },
    
    //get debts
    async getDebts() {
      var res = await this.$store.dispatch("home/getTotalDebtsBoth");
      if (res) {
        this.iOweIqd = res.iOweIqd;
        this.iOweUsd = res.iOweUsd;
        this.theyOweIqd = res.theyOweIqd;
        this.theyOweUsd = res.theyOweUsd;
      }
    },
    periodChanged(action, customStartDate = null, customEndDate = null) {
      this.period = action;

      if (action == "today") {
        this.startDate = moment().format("YYYY-MM-DD");
        this.endDate = moment().format("YYYY-MM-DD");
      } else if (action == "yesterday") {
        this.startDate = moment().subtract(1, "days").format("YYYY-MM-DD");
        this.endDate = moment().subtract(1, "days").format("YYYY-MM-DD");
      } else if (action == "last7days") {
        this.startDate = moment().subtract(7, "days").format("YYYY-MM-DD");
        this.endDate = moment().format("YYYY-MM-DD");
      } else if (action == "last30days") {
        this.startDate = moment().subtract(30, "days").format("YYYY-MM-DD");
        this.endDate = moment().format("YYYY-MM-DD");
      } else if (action == "thismonth") {
        this.startDate = moment().startOf("month").format("YYYY-MM-DD");
        this.endDate = moment().endOf("month").format("YYYY-MM-DD");
      } else if (action == "lastmonth") {
        this.startDate = moment()
          .subtract(1, "months")
          .startOf("month")
          .format("YYYY-MM-DD");
        this.endDate = moment()
          .subtract(1, "months")
          .endOf("month")
          .format("YYYY-MM-DD");
      } else if (action == "lastyear") {
        this.startDate = moment()
          .subtract(1, "years")
          .startOf("year")
          .format("YYYY-MM-DD");
        this.endDate = moment()
          .subtract(1, "years")
          .endOf("year")
          .format("YYYY-MM-DD");
      } else if (action == "thisyear") {
        this.startDate = moment().startOf("year").format("YYYY-MM-DD");
        this.endDate = moment().endOf("year").format("YYYY-MM-DD");
      } else if (action == "all") {
        this.startDate = "2000-01-01";
        this.endDate = moment().endOf("year").format("YYYY-MM-DD");
      } else if (action == "custom" && customStartDate && customEndDate) {
        this.startDate = customStartDate;
        this.endDate = customEndDate;
      }

      this.getData(1);
    },
    changeReportType() {
      this.saleType();
      this.mostSoldItem();
      this.incomes();
      this.expenses();
    },
    saleType() {
      var listServer = this.items.sale_type;
      //get top 5 items and for all other items make them one item sum
      if (listServer.length > 7) {
        var top7 = listServer.slice(0, 7);
        var other = listServer.slice(7);
        var sum = other.reduce((a, b) => {
          return {
            invoice_count:
              parseInt(a.invoice_count) + parseInt(b.invoice_count),
            st_name: this.$t("others"),
          };
        });
        top7.push(sum);
        listServer = top7;
      }

      var st_name = listServer.map((item) => item.st_name);
      var values = listServer.map((item) => {
        return parseFloat(item.invoice_count);
      });

      this.chartOptions = {
        ...this.chartOptions,
        labels: st_name,
      };
      this.series = values;
    },
    mostSoldItem() {
      var listServer = this.items.item_sale;
      //get top 5 items and for all other items make them one item sum
      if (listServer.length > 7) {
        var top7 = listServer.slice(0, 7);
        var other = listServer.slice(7);
        var sum = other.reduce((a, b) => {
          return {
            item_quantity_sold:
              parseInt(a.item_quantity_sold) + parseInt(b.item_quantity_sold),
            i_name: this.$t("others"),
          };
        });
        top7.push(sum);
        listServer = top7;
      }

      var i_name = listServer.map((item) => item.i_name);
      var values = listServer.map((item) => {
        return parseFloat(item.item_quantity_sold);
      });

      this.chartOptionsItems = {
        ...this.chartOptionsItems,
        labels: i_name,
      };
      this.seriesItems = values;
    },
    incomes() {
      var listServer = this.items.income_category;
      //get top 5 items and for all other items make them one item sum
      if (listServer.length > 7) {
        var top7 = listServer.slice(0, 7);
        var other = listServer.slice(7);
        var sum = other.reduce((a, b) => {
          return {
            total_income_number:
              parseInt(a.total_income_number) + parseInt(b.total_income_number),
            name: this.$t("others"),
          };
        });
        top7.push(sum);
        listServer = top7;
      }

      var name = listServer.map((item) => item.ic_name);
      var values = listServer.map((item) => {
        return parseFloat(item.total_income_number);
      });

      this.chartOptionsIncomes = {
        ...this.chartOptionsIncomes,
        labels: name,
      };
      this.seriesIncomes = values;
    },
    expenses() {
      var listServer = this.items.expense_category;
      //get top 5 items and for all other items make them one item sum
      if (listServer.length > 7) {
        var top7 = listServer.slice(0, 7);
        var other = listServer.slice(7);
        var sum = other.reduce((a, b) => {
          return {
            total_expense_number:
              parseInt(a.total_expense_number) +
              parseInt(b.total_expense_number),
            name: this.$t("others"),
          };
        });
        top7.push(sum);
        listServer = top7;
      }

      var name = listServer.map((item) => item.exc_name);
      var values = listServer.map((item) => {
        return parseFloat(item.total_expense_number);
      });

      this.chartOptionsExpenses = {
        ...this.chartOptionsExpenses,
        labels: name,
      };
      this.seriesExpenses = values;
    },
    processSalesTrendData() {
      console.log('processSalesTrendData called with:', this.salesTrend);
      if (!this.salesTrend || Object.keys(this.salesTrend).length === 0) {
        console.log('No sales trend data available');
        this.salesTrendData = [];
        return;
      }

      const series = [];
      let categories = [];

      // Process each currency
      Object.keys(this.salesTrend).forEach((currency) => {
        const trend = this.salesTrend[currency];
        
        if (trend.periods && trend.periods.length > 0) {
          // Set categories from the first currency (they should all be the same)
          if (categories.length === 0) {
            categories = trend.periods;
          }

          // Add series for this currency
          series.push({
            name: `${trend.currency} Sales`,
            data: trend.sales
          });
        }
      });

      // Update chart options with categories
      this.salesTrendChartOptions = {
        ...this.salesTrendChartOptions,
        xaxis: {
          ...this.salesTrendChartOptions.xaxis,
          categories: categories
        }
      };

      this.salesTrendData = series;
      console.log('Final chart data:', this.salesTrendData);
    },
    getDateRangeText() {
      if (!this.startDate || !this.endDate) {
        return this.$t("last_n_days");
      }
      
      const start = moment(this.startDate);
      const end = moment(this.endDate);
      const daysDiff = end.diff(start, 'days') + 1;
      
      if (daysDiff === 1) {
        return this.$t("today");
      } else if (daysDiff === 2 && start.format('YYYY-MM-DD') === moment().subtract(1, 'day').format('YYYY-MM-DD')) {
        return this.$t("yesterday");
      } else if (daysDiff <= 7) {
        return this.$t("last7days");
      } else if (daysDiff <= 30) {
        return this.$t("last30days");
      } else if (daysDiff <= 365) {
        return this.$t("thisyear");
      } else {
        return `${daysDiff} ${this.$t("days")}`;
      }
    },
    navigateToPage(path) {
      this.$router.push(path);
    },
 
    getPageDescription(id) {
      const descriptions = {
        dashboard: this.$t("overview_and_analytics"),
        "items-settings": this.$t("manage_item_categories"),
        "add-unit": this.$t("manage_item_units"),
        ca1: this.$t("manage_item_categories"),
        caw1: this.$t("manage_waste_categories"),
        froshtn: this.$t("configure_sale_types"),
        "add-type": this.$t("manage_sale_types"),
        "platform-froshtn": this.$t("manage_sale_platforms"),
        "platform-frosht1n": this.$t("manage_sale_cities"),
        items: this.$t("manage_inventory"),
        "add-item": this.$t("add_new_items"),
        "add-item-from-csv": this.$t("import_items_csv"),
        "item-list": this.$t("view_item_list"),
        "shortage-item": this.$t("view_shortage_items"),
        "expire-item": this.$t("view_expired_items"),
        "waste-item": this.$t("manage_waste_items"),
        "not-moved-items": this.$t("view_static_items"),
        box: this.$t("manage_boxes"),
        "add-box": this.$t("manage_boxes"),
        "exchange-box": this.$t("exchange_boxes"),
        "transfer-box": this.$t("transfer_boxes"),
        purchase: this.$t("manage_purchases"),
        "new-purchase": this.$t("create_purchase"),
        "purchase-list": this.$t("view_purchases"),
        "new-return-purchase": this.$t("return_to_supplier"),
        "return-purchases": this.$t("view_returned_purchases"),
        "sale-pos": this.$t("create_sale"),
        "sold-list": this.$t("view_sales"),
        "return-invoice": this.$t("manage_returns"),
        debt: this.$t("manage_debts"),
        "add-debt": this.$t("create_debt"),
        "debt-list": this.$t("view_debts"),
        expense: this.$t("manage_expenses"),
        "expense-category": this.$t("manage_expense_categories"),
        "add-expense": this.$t("create_expense"),
        "expense-list": this.$t("view_expenses"),
        income: this.$t("manage_incomes"),
        "income-category": this.$t("manage_income_categories"),
        "add-income": this.$t("create_income"),
        "income-list": this.$t("view_incomes"),
        report: this.$t("view_analytics"),
        "all-yearly-report": this.$t("annual_reports"),
        "category-report": this.$t("category_reports"),
        "city-report": this.$t("city_reports"),
        "platform-report": this.$t("platform_reports"),
        "sale-report": this.$t("sale_type_reports"),
        "employee-report": this.$t("employee_reports"),
        "item-report": this.$t("item_reports"),
        "loss-items-report": this.$t("loss_reports"),
        "customer-report": this.$t("customer_reports"),
        "waste-category-report": this.$t("waste_category_reports"),
        "waste-item-report": this.$t("waste_item_reports"),
        "expense-category-report": this.$t("expense_category_reports"),
        "expense-customer-report": this.$t("expense_customer_reports"),
        "income-customer-report": this.$t("income_customer_reports"),
        "income-category-report": this.$t("income_category_reports"),
        "debt-report": this.$t("debt_reports"),
        "suplier-report": this.$t("supplier_reports"),
        "purchase-item-report": this.$t("purchase_item_reports"),
        role: this.$t("manage_permissions"),
        "add-role": this.$t("create_role"),
        "role-list": this.$t("view_roles"),
        employee: this.$t("manage_staff"),
        customers: this.$t("manage_customers"),
        supliers: this.$t("manage_suppliers"),
        dolar_price: this.$t("manage_exchange_rates"),
        note: this.$t("manage_notes"),
        slider: this.$t("manage_sliders"),
        barcode: this.$t("generate_barcodes"),
        change_password: this.$t("update_password"),
        settings: this.$t("system_settings"),
        "history-log": this.$t("view_activity_logs"),
        faq: this.$t("frequently_asked_questions"),
      };
      return descriptions[id] || this.$t("access_feature");
    },
    getPageColor(id) {
      const colors = {
        dashboard: "blue",
        "items-settings": "green",
        "add-unit": "green",
        ca1: "green",
        caw1: "green",
        froshtn: "purple",
        "add-type": "purple",
        "platform-froshtn": "purple",
        "platform-frosht1n": "purple",
        items: "blue",
        "add-item": "blue",
        "add-item-from-csv": "blue",
        "item-list": "blue",
        "shortage-item": "blue",
        "expire-item": "blue",
        "waste-item": "blue",
        "not-moved-items": "blue",
        box: "red",
        "add-box": "red",
        "exchange-box": "red",
        "transfer-box": "red",
        purchase: "yellow",
        "new-purchase": "yellow",
        "purchase-list": "yellow",
        "new-return-purchase": "yellow",
        "return-purchases": "yellow",
        "sale-pos": "green",
        "sold-list": "green",
        "return-invoice": "green",
        debt: "pink",
        "add-debt": "pink",
        "debt-list": "pink",
        expense: "red",
        "expense-category": "red",
        "add-expense": "red",
        "expense-list": "red",
        income: "blue",
        "income-category": "blue",
        "add-income": "blue",
        "income-list": "blue",
        report: "cyan",
        "all-yearly-report": "cyan",
        "category-report": "cyan",
        "city-report": "cyan",
        "platform-report": "cyan",
        "sale-report": "cyan",
        "employee-report": "cyan",
        "item-report": "cyan",
        "loss-items-report": "cyan",
        "customer-report": "cyan",
        "waste-category-report": "cyan",
        "waste-item-report": "cyan",
        "expense-category-report": "cyan",
        "expense-customer-report": "cyan",
        "income-customer-report": "cyan",
        "income-category-report": "cyan",
        "debt-report": "cyan",
        "suplier-report": "cyan",
        "purchase-item-report": "cyan",
        role: "yellow",
        "add-role": "yellow",
        "role-list": "yellow",
        employee: "purple",
        customers: "blue",
        supliers: "indigo",
        dolar_price: "green",
        note: "pink",
        slider: "red",
        barcode: "purple",
        change_password: "blue",
        settings: "green",
        "history-log": "yellow",
        faq: "blue",
      };
      return colors[id] || "blue";
    },
    getSubItemIcon(id) {
      const icons = {
        "add-unit": "fa-solid fa-ruler",
        ca1: "fa-solid fa-tags",
        caw1: "fa-solid fa-trash",
        "add-type": "fa-solid fa-tag",
        "platform-froshtn": "fa-solid fa-store",
        "platform-frosht1n": "fa-solid fa-city",
        "add-item": "fa-solid fa-plus",
        "add-item-from-csv": "fa-solid fa-file-csv",
        "item-list": "fa-solid fa-list",
        "shortage-item": "fa-solid fa-exclamation-triangle",
        "expire-item": "fa-solid fa-clock",
        "waste-item": "fa-solid fa-trash-can",
        "not-moved-items": "fa-solid fa-pause",
        "add-box": "fa-solid fa-box",
        "exchange-box": "fa-solid fa-arrows-rotate",
        "transfer-box": "fa-solid fa-arrow-right-arrow-left",
        "new-purchase": "fa-solid fa-cart-plus",
        "purchase-list": "fa-solid fa-list",
        "new-return-purchase": "fa-solid fa-undo",
        "return-purchases": "fa-solid fa-list",
        "sale-pos": "fa-solid fa-cash-register",
        "sold-list": "fa-solid fa-list",
        "return-invoice": "fa-solid fa-undo",
        "add-debt": "fa-solid fa-plus",
        "debt-list": "fa-solid fa-list",
        "expense-category": "fa-solid fa-tags",
        "add-expense": "fa-solid fa-plus",
        "expense-list": "fa-solid fa-list",
        "income-category": "fa-solid fa-tags",
        "add-income": "fa-solid fa-plus",
        "income-list": "fa-solid fa-list",
        "all-yearly-report": "fa-solid fa-chart-line",
        "category-report": "fa-solid fa-chart-pie",
        "city-report": "fa-solid fa-map",
        "platform-report": "fa-solid fa-store",
        "sale-report": "fa-solid fa-chart-bar",
        "employee-report": "fa-solid fa-user-tie",
        "item-report": "fa-solid fa-box",
        "loss-items-report": "fa-solid fa-exclamation-triangle",
        "customer-report": "fa-solid fa-users",
        "waste-category-report": "fa-solid fa-trash",
        "waste-item-report": "fa-solid fa-trash-can",
        "expense-category-report": "fa-solid fa-tags",
        "expense-customer-report": "fa-solid fa-user",
        "income-customer-report": "fa-solid fa-user",
        "income-category-report": "fa-solid fa-tags",
        "debt-report": "fa-solid fa-file-invoice",
        "suplier-report": "fa-solid fa-truck",
        "purchase-item-report": "fa-solid fa-shopping-cart",
        "add-role": "fa-solid fa-plus",
        "role-list": "fa-solid fa-list",
      };
      return icons[id] || "fa-solid fa-circle";
    },
    getHoverBorderClass(color) {
      const classes = {
        blue: "hover:border-blue-300 dark:hover:border-blue-600",
        green: "hover:border-green-300 dark:hover:border-green-600",
        purple: "hover:border-purple-300 dark:hover:border-purple-600",
        orange: "hover:border-orange-300 dark:hover:border-orange-600",
        indigo: "hover:border-indigo-300 dark:hover:border-indigo-600",
        yellow: "hover:border-yellow-300 dark:hover:border-yellow-600",
        red: "hover:border-red-300 dark:hover:border-red-600",
        pink: "hover:border-pink-300 dark:hover:border-pink-600",
        teal: "hover:border-teal-300 dark:hover:border-teal-600",
        emerald: "hover:border-emerald-300 dark:hover:border-emerald-600",
        cyan: "hover:border-cyan-300 dark:hover:border-cyan-600",
        violet: "hover:border-violet-300 dark:hover:border-violet-600",
        amber: "hover:border-amber-300 dark:hover:border-amber-600",
        rose: "hover:border-rose-300 dark:hover:border-rose-600",
        lime: "hover:border-lime-300 dark:hover:border-lime-600",
        sky: "hover:border-sky-300 dark:hover:border-sky-600",
        stone: "hover:border-stone-300 dark:hover:border-stone-600",
        fuchsia: "hover:border-fuchsia-300 dark:hover:border-fuchsia-600",
        slate: "hover:border-slate-300 dark:hover:border-slate-600",
        zinc: "hover:border-zinc-300 dark:hover:border-zinc-600",
        neutral: "hover:border-neutral-300 dark:hover:border-neutral-600",
        gray: "hover:border-gray-300 dark:hover:border-gray-600",
      };
      return classes[color] || "hover:border-blue-300 dark:hover:border-blue-600";
    },
    getIconBgClass(color) {
      const classes = {
        blue: "bg-blue-100 dark:bg-blue-900/30",
        green: "bg-green-100 dark:bg-green-900/30",
        purple: "bg-purple-100 dark:bg-purple-900/30",
        orange: "bg-orange-100 dark:bg-orange-900/30",
        indigo: "bg-indigo-100 dark:bg-indigo-900/30",
        yellow: "bg-yellow-100 dark:bg-yellow-900/30",
        red: "bg-red-100 dark:bg-red-900/30",
        pink: "bg-pink-100 dark:bg-pink-900/30",
        teal: "bg-teal-100 dark:bg-teal-900/30",
        emerald: "bg-emerald-100 dark:bg-emerald-900/30",
        cyan: "bg-cyan-100 dark:bg-cyan-900/30",
        violet: "bg-violet-100 dark:bg-violet-900/30",
        amber: "bg-amber-100 dark:bg-amber-900/30",
        rose: "bg-rose-100 dark:bg-rose-900/30",
        lime: "bg-lime-100 dark:bg-lime-900/30",
        sky: "bg-sky-100 dark:bg-sky-900/30",
        stone: "bg-stone-100 dark:bg-stone-900/30",
        fuchsia: "bg-fuchsia-100 dark:bg-fuchsia-900/30",
        slate: "bg-slate-100 dark:bg-slate-900/30",
        zinc: "bg-zinc-100 dark:bg-zinc-900/30",
        neutral: "bg-neutral-100 dark:bg-neutral-900/30",
        gray: "bg-gray-100 dark:bg-gray-900/30",
      };
      return classes[color] || "bg-blue-100 dark:bg-blue-900/30";
    },
    getIconHoverBgClass(color) {
      const classes = {
        blue: "group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50",
        green: "group-hover:bg-green-200 dark:group-hover:bg-green-900/50",
        purple: "group-hover:bg-purple-200 dark:group-hover:bg-purple-900/50",
        orange: "group-hover:bg-orange-200 dark:group-hover:bg-orange-900/50",
        indigo: "group-hover:bg-indigo-200 dark:group-hover:bg-indigo-900/50",
        yellow: "group-hover:bg-yellow-200 dark:group-hover:bg-yellow-900/50",
        red: "group-hover:bg-red-200 dark:group-hover:bg-red-900/50",
        pink: "group-hover:bg-pink-200 dark:group-hover:bg-pink-900/50",
        teal: "group-hover:bg-teal-200 dark:group-hover:bg-teal-900/50",
        emerald: "group-hover:bg-emerald-200 dark:group-hover:bg-emerald-900/50",
        cyan: "group-hover:bg-cyan-200 dark:group-hover:bg-cyan-900/50",
        violet: "group-hover:bg-violet-200 dark:group-hover:bg-violet-900/50",
        amber: "group-hover:bg-amber-200 dark:group-hover:bg-amber-900/50",
        rose: "group-hover:bg-rose-200 dark:group-hover:bg-rose-900/50",
        lime: "group-hover:bg-lime-200 dark:group-hover:bg-lime-900/50",
        sky: "group-hover:bg-sky-200 dark:group-hover:bg-sky-900/50",
        stone: "group-hover:bg-stone-200 dark:group-hover:bg-stone-900/50",
        fuchsia: "group-hover:bg-fuchsia-200 dark:group-hover:bg-fuchsia-900/50",
        slate: "group-hover:bg-slate-200 dark:group-hover:bg-slate-900/50",
        zinc: "group-hover:bg-zinc-200 dark:group-hover:bg-zinc-900/50",
        neutral: "group-hover:bg-neutral-200 dark:group-hover:bg-neutral-900/50",
        gray: "group-hover:bg-gray-200 dark:group-hover:bg-gray-900/50",
      };
      return classes[color] || "group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50";
    },
    getIconTextClass(color) {
      const classes = {
        blue: "text-blue-600 dark:text-blue-400 text-xl",
        green: "text-green-600 dark:text-green-400 text-xl",
        purple: "text-purple-600 dark:text-purple-400 text-xl",
        orange: "text-orange-600 dark:text-orange-400 text-xl",
        indigo: "text-indigo-600 dark:text-indigo-400 text-xl",
        yellow: "text-yellow-600 dark:text-yellow-400 text-xl",
        red: "text-red-600 dark:text-red-400 text-xl",
        pink: "text-pink-600 dark:text-pink-400 text-xl",
        teal: "text-teal-600 dark:text-teal-400 text-xl",
        emerald: "text-emerald-600 dark:text-emerald-400 text-xl",
        cyan: "text-cyan-600 dark:text-cyan-400 text-xl",
        violet: "text-violet-600 dark:text-violet-400 text-xl",
        amber: "text-amber-600 dark:text-amber-400 text-xl",
        rose: "text-rose-600 dark:text-rose-400 text-xl",
        lime: "text-lime-600 dark:text-lime-400 text-xl",
        sky: "text-sky-600 dark:text-sky-400 text-xl",
        stone: "text-stone-600 dark:text-stone-400 text-xl",
        fuchsia: "text-fuchsia-600 dark:text-fuchsia-400 text-xl",
        slate: "text-slate-600 dark:text-slate-400 text-xl",
        zinc: "text-zinc-600 dark:text-zinc-400 text-xl",
        neutral: "text-neutral-600 dark:text-neutral-400 text-xl",
        gray: "text-gray-600 dark:text-gray-400 text-xl",
      };
      return classes[color] || "text-blue-600 dark:text-blue-400 text-xl";
    },
  },
};
</script>
<style scoped>
/* Modern Table Styles */
.modern-light-table {
  --easy-table-border: 1px solid #e5e7eb;
  --easy-table-row-border: 1px solid #f3f4f6;
  --easy-table-header-font-size: 0.875rem;
  --easy-table-header-height: 3rem;
  --easy-table-header-font-color: #374151;
  --easy-table-header-background-color: #f8fafc;
  --easy-table-body-even-row-font-color: #111827;
  --easy-table-body-even-row-background-color: #ffffff;
  --easy-table-body-odd-row-font-color: #111827;
  --easy-table-body-odd-row-background-color: #f8fafc;
  --easy-table-body-row-font-size: 0.875rem;
  --easy-table-body-row-height: 3.5rem;
  --easy-table-body-row-hover-font-color: #111827;
  --easy-table-body-row-hover-background-color: #f1f5f9;
  --easy-table-scrollbar-track-color: #f1f5f9;
  --easy-table-scrollbar-color: #cbd5e1;
  --easy-table-scrollbar-thumb-color: #94a3b8;
  --easy-table-scrollbar-corner-color: #f1f5f9;
  --easy-table-loading-mask-background-color: rgba(255, 255, 255, 0.8);
}

/* Dark mode table styles */
.dark .modern-dark-table {
  --easy-table-border: 1px solid #1f2937;
  --easy-table-row-border: 1px solid #1f2937;
  --easy-table-header-font-size: 0.875rem;
  --easy-table-header-height: 3rem;
  --easy-table-header-font-color: #f9fafb;
  --easy-table-header-background-color: #0f172a;
  --easy-table-body-even-row-font-color: #f9fafb;
  --easy-table-body-even-row-background-color: #334155;
  --easy-table-body-odd-row-font-color: #f9fafb;
  --easy-table-body-odd-row-background-color: #334155;
  --easy-table-body-row-font-size: 0.875rem;
  --easy-table-body-row-height: 3.5rem;
  --easy-table-body-row-hover-font-color: #f9fafb;
  --easy-table-body-row-hover-background-color: #475569;
  --easy-table-scrollbar-track-color: #0f172a;
  --easy-table-scrollbar-color: #1f2937;
  --easy-table-scrollbar-thumb-color: #475569;
  --easy-table-scrollbar-corner-color: #0f172a;
  --easy-table-loading-mask-background-color: #252c47;
}

/* Table styling */
.modern-table {
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

.modern-table th {
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
}

.modern-table td {
  padding: 0.75rem 1rem;
  vertical-align: middle;
}

/* Custom scrollbar */
.modern-table::-webkit-scrollbar {
  height: 8px;
}

.modern-table::-webkit-scrollbar-track {
  background: var(--easy-table-scrollbar-track-color);
  border-radius: 4px;
}

.modern-table::-webkit-scrollbar-thumb {
  background: var(--easy-table-scrollbar-thumb-color);
  border-radius: 4px;
}

.modern-table::-webkit-scrollbar-thumb:hover {
  background: var(--easy-table-scrollbar-color);
}

/* Dark mode overrides */
::v-deep(.modern-dark-table .vue3-easy-data-table__row),
::v-deep(.modern-dark-table .vue3-easy-data-table__body td) {
  background-color: #334155 !important;
  color: #f9fafb !important;
  border-color: #1f2937 !important;
}

::v-deep(.modern-dark-table .vue3-easy-data-table__row:hover),
::v-deep(.modern-dark-table .vue3-easy-data-table__row:hover td) {
  background-color: #475569 !important;
}

::v-deep(.modern-dark-table .vue3-easy-data-table__header th) {
  background-color: #0f172a !important;
  color: #f9fafb !important;
  border-color: #1f2937 !important;
}

::v-deep(.modern-dark-table .vue3-easy-data-table__footer),
::v-deep(.modern-dark-table .vue3-easy-data-table__pagination),
::v-deep(.modern-dark-table .vue3-easy-data-table__rows-selector) {
  background-color: #0f172a !important;
  color: #f9fafb !important;
  border-top: 1px solid #1f2937 !important;
}

::v-deep(.modern-dark-table .vue3-easy-data-table__rows-selector select),
::v-deep(.modern-dark-table .vue3-easy-data-table__rows-selector input) {
  background-color: #1f2937 !important;
  color: #f9fafb !important;
  border-color: #334155 !important;
}

::v-deep(.modern-dark-table .vue3-easy-data-table__pagination button) {
  background-color: #1f2937 !important;
  color: #f9fafb !important;
  border: 1px solid #334155 !important;
}

::v-deep(
    .modern-dark-table .vue3-easy-data-table__pagination button.is-active
  ) {
  background-color: #334155 !important;
  color: #f9fafb !important;
}

::v-deep(.modern-dark-table .vue3-easy-data-table__pagination button:hover) {
  background-color: #475569 !important;
}

::v-deep(.modern-dark-table .vue3-easy-data-table__loading-mask) {
  background-color: #252c47 !important;
  backdrop-filter: blur(2px);
}

/* Chart container improvements */
.chart-container {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.dark .chart-container {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3),
    0 2px 4px -1px rgba(0, 0, 0, 0.2);
}

/* Chart title styling */
.chart-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
  text-align: center;
}

.dark .chart-title {
  color: #f9fafb;
}

/* Chart legend improvements */
.apexcharts-legend {
  padding: 0 1rem !important;
}

.apexcharts-legend-series {
  margin: 0.25rem 0 !important;
}

/* Responsive chart sizing */
@media (max-width: 768px) {
  .chart-container {
    padding: 1rem;
  }

  .chart-title {
    font-size: 1rem;
  }
}
</style>
